import React, { useState, useEffect, useMemo } from 'react';
import { BarChart3, Package, Users, ShoppingCart, FileText, Star, ChevronDown, ChevronUp, Check, X, Edit2, Trash2, Plus, Save, Eye, EyeOff, Key, Mail, RotateCcw, Code, Settings, Truck, Tag, Percent, Euro, TrendingUp, CreditCard, Banknote, DollarSign, Palette, Search, Globe, BookOpen, Share2, Link2, Database, Download, Upload, Clock, Copy, Lock } from 'lucide-react';
import { apiClient, formatApiError } from '../context/AuthContext';
import DOMPurify from 'dompurify';
import toast from 'react-hot-toast';
import ImageUploader from '../components/ImageUploader';
import PerformanceAnalytics from '../components/PerformanceAnalytics';
import SEOEditor from '../components/SEOEditor';
import SEOPagesManager from '../components/SEOPagesManager';
import BlogManager from '../components/BlogManager';
import ReviewsManager from '../components/ReviewsManager';
import ProductBadge, { BADGE_ICONS, BADGE_COLORS, BADGE_COLOR_LABELS } from '../components/ProductBadge';
import VideoUploader from '../components/VideoUploader';

const STATUS_OPTIONS = ['pending_payment', 'pending', 'processing', 'shipped', 'delivered', 'cancelled', 'refunded'];
const STATUS_LABELS = {
  pending_payment: { label: 'En attente paiement', color: 'bg-orange-100 text-orange-700' },
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700' },
  processing: { label: 'En préparation', color: 'bg-blue-100 text-blue-700' },
  shipped: { label: 'Expédié', color: 'bg-purple-100 text-purple-700' },
  delivered: { label: 'Livré', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700' },
  refunded: { label: 'Remboursé', color: 'bg-gray-100 text-gray-700' },
  approved: { label: 'Approuvé', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejeté', color: 'bg-red-100 text-red-700' }
};

const CATEGORIES = ['sunglasses', 'optical', 'camera'];
const ROLES = ['user', 'pro', 'admin'];

// Multiple Payment Methods Modal
function MultiplePaymentModal({ order, onClose, onSave }) {
  const [methods, setMethods] = useState([
    { method: 'Carte bancaire', amount: 0 },
  ]);
  const [loading, setLoading] = useState(false);

  const totalAmount = order?.total_ttc || 0;
  const paidAmount = methods.reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0);
  const remaining = totalAmount - paidAmount;

  const addMethod = () => {
    setMethods([...methods, { method: 'Espèces', amount: 0 }]);
  };

  const removeMethod = (index) => {
    setMethods(methods.filter((_, i) => i !== index));
  };

  const updateMethod = (index, field, value) => {
    const updated = [...methods];
    updated[index][field] = value;
    setMethods(updated);
  };

  const handleSubmit = async () => {
    if (Math.abs(remaining) > 0.01) {
      toast.error(`Il reste ${remaining.toFixed(2)}€ à payer`);
      return;
    }
    
    setLoading(true);
    try {
      await onSave(methods);
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">Marquer comme payé - Paiements multiples</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-blue-900">Montant total de la commande : {totalAmount.toFixed(2)}€</p>
            <p className={`text-sm mt-1 ${remaining === 0 ? 'text-green-600' : remaining > 0 ? 'text-orange-600' : 'text-red-600'}`}>
              {remaining > 0 ? `Reste à payer : ${remaining.toFixed(2)}€` : remaining < 0 ? `Trop payé : ${Math.abs(remaining).toFixed(2)}€` : '✓ Montant exact'}
            </p>
          </div>

          {methods.map((method, index) => (
            <div key={index} className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-700">Mode de paiement {index + 1}</h4>
                {methods.length > 1 && (
                  <button onClick={() => removeMethod(index)} className="text-red-500 hover:text-red-600">
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Méthode</label>
                  <select
                    value={method.method}
                    onChange={(e) => updateMethod(index, 'method', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="Carte bancaire">Carte bancaire</option>
                    <option value="Espèces">Espèces</option>
                    <option value="Chèque">Chèque</option>
                    <option value="Virement">Virement bancaire</option>
                    <option value="PayPal">PayPal</option>
                    <option value="Stripe">Stripe</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Montant (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={method.amount}
                    onChange={(e) => updateMethod(index, 'amount', parseFloat(e.target.value) || 0)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>
          ))}

          <button
            onClick={addMethod}
            className="w-full border-2 border-dashed border-gray-300 rounded-xl py-3 text-gray-500 hover:border-[#00aaff] hover:text-[#00aaff] font-medium flex items-center justify-center gap-2"
          >
            <Plus size={16} /> Ajouter un mode de paiement
          </button>

          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || remaining !== 0}
              className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Enregistrement...' : 'Marquer comme payé'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Category Form Modal
function CategoryModal({ category, onClose, onSave }) {
  const [form, setForm] = useState(category ? {
    name: category.name || '',
    slug: category.slug || '',
    description: category.description || '',
    icon: category.icon || '',
    is_active: category.is_active !== undefined ? category.is_active : true,
    order: category.order || 0
  } : {
    name: '', slug: '', description: '', icon: '', is_active: true, order: 0
  });

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(form);
      onClose();
    } catch (error) {
      console.error('Error saving category:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-générer le slug depuis le nom
  const handleNameChange = (name) => {
    setForm({...form, name});
    if (!category) { // Seulement pour les nouvelles catégories
      const slug = name.toLowerCase()
        .replace(/[àáâãäå]/g, 'a')
        .replace(/[èéêë]/g, 'e')
        .replace(/[ìíîï]/g, 'i')
        .replace(/[òóôõö]/g, 'o')
        .replace(/[ùúûü]/g, 'u')
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      setForm(prev => ({...prev, slug}));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">
            {category ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Nom *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              required
              placeholder="Ex: Lunettes de soleil"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Slug * <span className="text-xs text-gray-500">(URL: /collection/{form.slug || '...'})</span>
            </label>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => setForm({...form, slug: e.target.value})}
              required
              placeholder="lunettes-de-soleil"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              rows={3}
              placeholder="Description de la catégorie"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Icône (emoji) <span className="text-xs text-gray-500">Ex: 🕶️ 👓 📸</span>
            </label>
            <input
              type="text"
              value={form.icon}
              onChange={(e) => setForm({...form, icon: e.target.value})}
              placeholder="🕶️"
              maxLength={2}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Ordre d'affichage</label>
              <input
                type="number"
                value={form.order}
                onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({...form, is_active: e.target.checked})}
                  className="w-4 h-4 accent-[#00aaff]"
                />
                <span className="text-sm text-gray-700">Catégorie active</span>
              </label>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-[#00aaff] text-white rounded-lg hover:bg-[#0088dd] font-medium disabled:opacity-50"
            >
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Product Form Modal
function ProductModal({ product, attributes, categories, onClose, onSave }) {
  // Get available colors from attributes - now each attribute IS a color
  const availableColors = attributes?.filter(attr => attr.value && attr.name) || [];

  const [form, setForm] = useState(product ? {
    ...product,
    categories: product.categories || [],
    colors: product.colors || [],
    features: product.features || '',
    lens_options: product.lens_options || [],
    color_images: product.color_images || {},
    has_lens_config: product.has_lens_config || false,
    display_by_color: product.display_by_color || false,
    blue_light_filter: product.blue_light_filter || false,
    custom_badge: product.custom_badge || { enabled: false, text: '', color: 'violet', icon: 'shield' },
    homepage_order: product.homepage_order ?? null,
    videos: product.videos || [],
    // SEO fields
    meta_title: product.meta_title || '',
    meta_description: product.meta_description || '',
    slug: product.slug || '',
    og_image: product.og_image || ''
  } : {
    name: '', subtitle: '', description: '', price: '', categories: [],
    image_url: '', features: '', colors: [], stock: 100, is_new: false, is_bestseller: false, featured: false,
    has_lens_config: false, lens_options: [], color_images: {}, display_by_color: false,
    blue_light_filter: false,
    custom_badge: { enabled: false, text: '', color: 'violet', icon: 'shield' },
    homepage_order: null,
    videos: [],
    meta_title: '', meta_description: '', slug: '', og_image: ''
  });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [newLensOption, setNewLensOption] = useState({ name: '', description: '', price_modifier: 0, is_reimbursable: false, emoji: '' });
  const [selectedColorForImages, setSelectedColorForImages] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  // Get colors array (handle both old string format and new array format)
  const colorsArray = Array.isArray(form.colors) 
    ? form.colors 
    : (typeof form.colors === 'string' && form.colors ? form.colors.split(',').map(c => c.trim()).filter(Boolean) : []);

  const toggleColor = (color) => {
    const currentColors = Array.isArray(form.colors) ? form.colors : [];
    const colorExists = currentColors.some(c => 
      (typeof c === 'string' ? c : c.name) === color.name
    );

    if (colorExists) {
      setForm({
        ...form,
        colors: currentColors.filter(c => 
          (typeof c === 'string' ? c : c.name) !== color.name
        )
      });
    } else {
      setForm({
        ...form,
        colors: [...currentColors, { name: color.name, value: color.value, slug: color.slug }]
      });
    }
  };

  const isColorSelected = (color) => {
    if (!Array.isArray(form.colors)) return false;
    return form.colors.some(c => 
      (typeof c === 'string' ? c : c.name) === color.name
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...form,
        price: parseFloat(form.price),
        stock: parseInt(form.stock),
        features: typeof form.features === 'string' ? form.features.split(',').map(f => f.trim()).filter(Boolean) : form.features,
        colors: colorsArray,
        lens_options: form.lens_options || [],
        color_images: form.color_images || {},
        // Meta description always mirrors the product description
        meta_description: form.description || ''
      };
      
      if (product?.id) {
        await apiClient.put(`/products/${product.id}`, data);
        toast.success('Produit mis à jour');
      } else {
        await apiClient.post('/products', data);
        toast.success('Produit créé');
      }
      onSave();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const addLensOption = () => {
    if (!newLensOption.name) return;
    setForm({
      ...form,
      lens_options: [...(form.lens_options || []), { ...newLensOption, price_modifier: parseFloat(newLensOption.price_modifier) || 0 }]
    });
    setNewLensOption({ name: '', description: '', price_modifier: 0, is_reimbursable: false, emoji: '' });
  };

  const removeLensOption = (index) => {
    setForm({
      ...form,
      lens_options: form.lens_options.filter((_, i) => i !== index)
    });
  };

  const addImageToColor = () => {
    if (!selectedColorForImages || !newImageUrl) return;
    const colorImages = { ...(form.color_images || {}) };
    if (!colorImages[selectedColorForImages]) {
      colorImages[selectedColorForImages] = [];
    }
    colorImages[selectedColorForImages].push(newImageUrl);
    setForm({ ...form, color_images: colorImages });
    setNewImageUrl('');
  };

  const handleImageUpload = async (e, color) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await apiClient.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Add uploaded image URL to color images
      const colorImages = { ...(form.color_images || {}) };
      if (!colorImages[color]) {
        colorImages[color] = [];
      }
      // Construct full URL with backend domain
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const fullImageUrl = data.url.startsWith('http') ? data.url : `${backendUrl}${data.url}`;
      colorImages[color].push(fullImageUrl);
      setForm({ ...form, color_images: colorImages });
      
      toast.success('Image uploadée avec succès');
    } catch (error) {
      toast.error('Erreur lors de l\'upload de l\'image');
      console.error('Upload error:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImageFromColor = (color, imageIndex) => {
    const colorImages = { ...(form.color_images || {}) };
    if (colorImages[color]) {
      colorImages[color] = colorImages[color].filter((_, i) => i !== imageIndex);
      if (colorImages[color].length === 0) {
        delete colorImages[color];
      }
    }
    setForm({ ...form, color_images: colorImages });
  };

  // Drag & Drop states for color images reordering
  const [draggedColorImageIndex, setDraggedColorImageIndex] = useState(null);
  const [dragOverColorImageIndex, setDragOverColorImageIndex] = useState(null);
  const [draggedColorName, setDraggedColorName] = useState(null);

  const handleColorImageDragStart = (e, colorName, index) => {
    setDraggedColorImageIndex(index);
    setDraggedColorName(colorName);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleColorImageDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColorImageIndex(index);
  };

  const handleColorImageDrop = (e, colorName, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (draggedColorImageIndex === null || draggedColorImageIndex === dropIndex || draggedColorName !== colorName) {
      setDraggedColorImageIndex(null);
      setDragOverColorImageIndex(null);
      setDraggedColorName(null);
      return;
    }

    const colorImages = { ...(form.color_images || {}) };
    const images = [...(colorImages[colorName] || [])];
    
    const [draggedItem] = images.splice(draggedColorImageIndex, 1);
    images.splice(dropIndex, 0, draggedItem);
    
    colorImages[colorName] = images;
    setForm({ ...form, color_images: colorImages });
    
    setDraggedColorImageIndex(null);
    setDragOverColorImageIndex(null);
    setDraggedColorName(null);
  };

  const handleColorImageDragEnd = () => {
    setDraggedColorImageIndex(null);
    setDragOverColorImageIndex(null);
    setDraggedColorName(null);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-900">{product?.id ? 'Modifier le produit' : 'Nouveau produit'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 border-b border-gray-100 flex gap-2">
          {[
            { key: 'general', label: 'Général' },
            { key: 'images', label: 'Images' },
            { key: 'lenses', label: 'Config. Verres' },
            { key: 'seo', label: 'SEO' }
          ].map(tab => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium rounded-t-xl transition-all ${
                activeTab === tab.key
                  ? 'bg-[#00aaff] text-white'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* General Tab */}
          {activeTab === 'general' && (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Nom *</label>
                  <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Sous-titre</label>
                  <input value={form.subtitle || ''} onChange={e => setForm({...form, subtitle: e.target.value})}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Prix public (€) *</label>
                  <input type="number" step="0.01" value={form.price} onChange={e => setForm({...form, price: e.target.value})} required
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">
                    Prix Pro (€) 
                    <span className="ml-2 bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full">
                      ⭐ TARIF PRÉFÉRENTIEL
                    </span>
                  </label>
                  <input 
                    type="number" 
                    step="0.01" 
                    value={form.pro_price || ''} 
                    onChange={e => setForm({...form, pro_price: e.target.value})} 
                    placeholder="Tarif pour les professionnels"
                    className="w-full border border-amber-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-500 bg-amber-50/30" 
                  />
                  {form.price && form.pro_price && parseFloat(form.pro_price) < parseFloat(form.price) && (
                    <p className="text-xs text-green-600 mt-1 font-semibold">
                      ✓ Économie pro : {(parseFloat(form.price) - parseFloat(form.pro_price)).toFixed(2)}€ 
                      (-{Math.round(((parseFloat(form.price) - parseFloat(form.pro_price)) / parseFloat(form.price)) * 100)}%)
                    </p>
                  )}
                </div>
                
                {/* Multi-categories selection */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                    Catégories (sélection multiple)
                  </label>
                  <div className="border border-gray-200 rounded-xl p-3 space-y-2 max-h-48 overflow-y-auto">
                    {categories && categories.length > 0 ? (
                      categories.map(cat => {
                        const catSlug = cat.slug;
                        return (
                          <label key={catSlug} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg">
                            <input
                              type="checkbox"
                              checked={form.categories?.includes(catSlug)}
                              onChange={(e) => {
                                const newCategories = e.target.checked
                                  ? [...(form.categories || []), catSlug]
                                  : (form.categories || []).filter(s => s !== catSlug);
                                setForm({...form, categories: newCategories});
                              }}
                              className="w-4 h-4 accent-[#00aaff]"
                            />
                            <span className="text-sm flex items-center gap-2">
                              {cat.icon && <span>{cat.icon}</span>}
                              <span className="font-medium">{cat.name}</span>
                              <code className="text-xs bg-gray-100 px-1 rounded">/collection/{cat.slug}</code>
                            </span>
                          </label>
                        );
                      })
                    ) : (
                      <p className="text-xs text-gray-400">Aucune catégorie disponible. Créez-en dans l'onglet Catégories.</p>
                    )}
                  </div>
                  {form.categories && form.categories.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      ✓ {form.categories.length} catégorie(s) sélectionnée(s)
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Stock</label>
                  <input type="number" value={form.stock} onChange={e => setForm({...form, stock: e.target.value})}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]" />
                </div>
              </div>
              
              {/* Image Upload Section */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                  Images du produit 📸
                </label>
                <ImageUploader
                  images={form.images || []}
                  onChange={(newImages) => setForm({...form, images: newImages})}
                  maxImages={10}
                />
                <p className="text-xs text-gray-400 mt-2">
                  💡 La première image sera utilisée comme image principale
                </p>
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Description</label>
                <textarea value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} rows={3}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff] resize-none" />
              </div>
              
              {/* Colors Selection */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-3 uppercase">
                  Couleurs disponibles
                </label>
                
                {availableColors.length === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                    <p className="font-semibold mb-1">⚠️ Aucune couleur définie</p>
                    <p className="text-xs">
                      Allez dans l'onglet <strong>Attributs</strong> pour créer des couleurs réutilisables.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {availableColors.map((color, index) => {
                      const isSelected = isColorSelected(color);
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => toggleColor(color)}
                          className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                            isSelected 
                              ? 'border-[#00aaff] bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div
                            className="w-8 h-8 rounded-lg border-2 border-gray-200 flex-shrink-0"
                            style={{ backgroundColor: color.value }}
                          />
                          <div className="flex-1 text-left">
                            <p className={`text-sm font-semibold ${isSelected ? 'text-[#00aaff]' : 'text-gray-700'}`}>
                              {color.name}
                            </p>
                            <p className="text-xs text-gray-500 font-mono">{color.value}</p>
                          </div>
                          {isSelected && (
                            <Check size={18} className="text-[#00aaff]" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                
                {/* Selected colors preview */}
                {colorsArray.length > 0 && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-gray-600">
                        Couleurs sélectionnées ({colorsArray.length}) :
                      </p>
                      <button
                        type="button"
                        onClick={async () => {
                          if (window.confirm('Voulez-vous vraiment supprimer toutes les couleurs de ce produit ?')) {
                            try {
                              if (product?._id) {
                                await apiClient.delete(`/products/${product._id}/colors`);
                              }
                              setForm({...form, colors: []});
                            } catch (error) {
                              console.error('Erreur:', error);
                              alert('Erreur lors de la suppression des couleurs');
                            }
                          }
                        }}
                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                      >
                        Tout supprimer
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-500 mb-3 leading-snug">
                      💡 Laissez les champs <strong>Prix public</strong> et <strong>Prix pro</strong> vides pour utiliser les prix du produit par défaut ({form.price || 0}€ / {form.pro_price || '—'}€).
                    </p>
                    <div className="space-y-2">
                      {colorsArray.map((c, i) => {
                        const colorObj = typeof c === 'string'
                          ? (availableColors.find(ac => ac.name === c) || { name: c, value: '#ccc' })
                          : c;
                        const updateColorField = (field, rawValue) => {
                          const next = Array.isArray(form.colors) ? [...form.colors] : [];
                          // Normalize existing entry to object form
                          const existing = typeof next[i] === 'string'
                            ? { name: next[i], value: colorObj.value, slug: colorObj.slug }
                            : { ...next[i] };
                          if (rawValue === '' || rawValue === null || rawValue === undefined) {
                            delete existing[field];
                          } else {
                            const num = Number(rawValue);
                            if (!Number.isNaN(num) && num >= 0) existing[field] = num;
                          }
                          next[i] = existing;
                          setForm({ ...form, colors: next });
                        };
                        const priceVal = (typeof c === 'object' && c?.price !== undefined && c?.price !== null) ? c.price : '';
                        const proPriceVal = (typeof c === 'object' && c?.pro_price !== undefined && c?.pro_price !== null) ? c.pro_price : '';
                        return (
                          <div
                            key={i}
                            className="flex items-center gap-3 bg-white rounded-lg border border-gray-200 px-3 py-2"
                            data-testid={`color-row-${i}`}
                          >
                            <div
                              className="w-6 h-6 rounded border border-gray-200 flex-shrink-0"
                              style={{ backgroundColor: colorObj?.value || '#ccc' }}
                            />
                            <span className="text-xs font-semibold text-gray-800 min-w-[90px]">
                              {colorObj.name}
                            </span>
                            <div className="flex items-center gap-1 flex-1">
                              <label className="text-[10px] uppercase font-semibold text-gray-500 whitespace-nowrap">Prix public</label>
                              <div className="relative flex-1">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={priceVal}
                                  placeholder={form.price ? String(form.price) : '—'}
                                  onChange={(e) => updateColorField('price', e.target.value)}
                                  data-testid={`color-price-${i}`}
                                  className="w-full border border-gray-200 rounded px-2 py-1 pr-5 text-xs outline-none focus:border-[#00aaff]"
                                />
                                <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">€</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-1">
                              <label className="text-[10px] uppercase font-semibold text-gray-500 whitespace-nowrap">Prix pro</label>
                              <div className="relative flex-1">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={proPriceVal}
                                  placeholder={form.pro_price ? String(form.pro_price) : '—'}
                                  onChange={(e) => updateColorField('pro_price', e.target.value)}
                                  data-testid={`color-pro-price-${i}`}
                                  className="w-full border border-gray-200 rounded px-2 py-1 pr-5 text-xs outline-none focus:border-amber-500"
                                />
                                <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">€</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Caractéristiques (séparées par virgule)</label>
                <input value={Array.isArray(form.features) ? form.features.join(', ') : form.features || ''} 
                  onChange={e => setForm({...form, features: e.target.value})}
                  placeholder="Bluetooth 5.3, WiFi 6E, Autonomie 72h"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]" />
              </div>
              <div className="flex gap-4 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_new || false} onChange={e => setForm({...form, is_new: e.target.checked})}
                    className="w-4 h-4 accent-[#00aaff]" />
                  <span className="text-sm text-gray-600">Nouveau</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_bestseller || false} onChange={e => setForm({...form, is_bestseller: e.target.checked})}
                    className="w-4 h-4 accent-[#00aaff]" />
                  <span className="text-sm text-gray-600">Bestseller</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.featured || false} onChange={e => setForm({...form, featured: e.target.checked})}
                    className="w-4 h-4 accent-green-500" />
                  <span className="text-sm text-gray-600">⭐ En vedette (page d'accueil)</span>
                </label>
                <label className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Ordre page d'accueil :</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={form.homepage_order ?? ''}
                    onChange={e => setForm({ ...form, homepage_order: e.target.value === '' ? null : Number(e.target.value) })}
                    placeholder="ex: 1"
                    className="w-20 border border-gray-200 rounded-lg px-2 py-1 text-sm outline-none focus:border-[#00aaff]"
                    data-testid="product-homepage-order"
                    title="Plus le chiffre est petit, plus le produit apparaît tôt sur la page d'accueil. Laisser vide pour ne pas forcer l'ordre."
                  />
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.display_by_color || false} onChange={e => setForm({...form, display_by_color: e.target.checked})}
                    className="w-4 h-4 accent-purple-500" />
                  <span className="text-sm text-gray-600">📋 Afficher par couleur dans le catalogue</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-blue-light-filter">
                  <input type="checkbox" checked={form.blue_light_filter || false} onChange={e => setForm({...form, blue_light_filter: e.target.checked})}
                    className="w-4 h-4 accent-violet-500" />
                  <span className="text-sm text-gray-600">🛡️ Filtre lumière bleue intégré (badge violet sur le produit)</span>
                </label>
              </div>
              {form.display_by_color && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-3">
                  <p className="text-xs text-purple-700">
                    ℹ️ Ce produit apparaîtra plusieurs fois dans le catalogue : une fois par couleur avec le nom "Titre - Couleur"
                  </p>
                </div>
              )}

              {/* Videos */}
              <VideoUploader
                videos={form.videos || []}
                onChange={(videos) => setForm({ ...form, videos })}
              />

              {/* Custom product badge */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3" data-testid="custom-badge-editor">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.custom_badge?.enabled || false}
                    onChange={e => setForm({
                      ...form,
                      custom_badge: { ...(form.custom_badge || { text: '', color: 'violet', icon: 'shield' }), enabled: e.target.checked }
                    })}
                    className="w-4 h-4 accent-[#00aaff]"
                    data-testid="custom-badge-toggle"
                  />
                  <span className="text-sm font-semibold text-gray-800">🏷️ Badge personnalisé sur ce produit</span>
                </label>

                {form.custom_badge?.enabled && (
                  <div className="pl-6 space-y-3">
                    {/* Text */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Texte du badge</label>
                      <input
                        type="text"
                        value={form.custom_badge?.text || ''}
                        onChange={e => setForm({ ...form, custom_badge: { ...form.custom_badge, text: e.target.value } })}
                        placeholder="ex: Livraison gratuite, Nouveauté 2026…"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                        data-testid="custom-badge-text"
                      />
                    </div>

                    {/* Color palette */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Couleur</label>
                      <div className="flex flex-wrap gap-2">
                        {Object.keys(BADGE_COLORS).map(colorKey => {
                          const palette = BADGE_COLORS[colorKey];
                          const active = form.custom_badge?.color === colorKey;
                          return (
                            <button
                              type="button"
                              key={colorKey}
                              onClick={() => setForm({ ...form, custom_badge: { ...form.custom_badge, color: colorKey } })}
                              title={BADGE_COLOR_LABELS[colorKey]}
                              className={`w-9 h-9 rounded-full ${palette.bg} border-2 ${active ? 'border-[#00aaff] scale-110 shadow-md' : 'border-transparent'} transition-all hover:scale-105`}
                              data-testid={`custom-badge-color-${colorKey}`}
                            >
                              <span className={`block w-full h-full rounded-full ${palette.icon}`} style={{ boxShadow: active ? 'inset 0 0 0 2px currentColor' : 'inset 0 0 0 1px currentColor' }} />
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Icon picker */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Icône</label>
                      <div className="grid grid-cols-11 gap-1.5">
                        {Object.entries(BADGE_ICONS).map(([key, IconComp]) => {
                          const active = form.custom_badge?.icon === key;
                          return (
                            <button
                              type="button"
                              key={key}
                              onClick={() => setForm({ ...form, custom_badge: { ...form.custom_badge, icon: key } })}
                              title={key}
                              className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-all ${active ? 'bg-[#00aaff] border-[#00aaff] text-white shadow-md' : 'bg-white border-gray-200 text-gray-600 hover:border-[#00aaff] hover:text-[#00aaff]'}`}
                              data-testid={`custom-badge-icon-${key}`}
                            >
                              <IconComp size={15} />
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Live preview */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Aperçu</label>
                      <div className="bg-white border border-gray-200 rounded-xl p-4">
                        {form.custom_badge?.text ? (
                          <ProductBadge badge={form.custom_badge} size="md" />
                        ) : (
                          <p className="text-xs text-gray-400 italic">Saisissez un texte pour voir l'aperçu</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Images Tab */}
          {activeTab === 'images' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-700">
                  <strong>Images par couleur :</strong> Ajoutez des photos spécifiques pour chaque couleur. 
                  Quand un client sélectionne une couleur, les photos correspondantes s'afficheront.
                </p>
              </div>

              {colorsArray.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Ajoutez d'abord des couleurs dans l'onglet "Général"</p>
                </div>
              ) : (
                <>
                  {/* Add image to color */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs font-semibold text-gray-500 mb-3 uppercase">Ajouter une image</p>
                    
                    {/* Color Selection */}
                    <select
                      value={selectedColorForImages}
                      onChange={e => setSelectedColorForImages(e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff] mb-3"
                    >
                      <option value="">Choisir une couleur</option>
                      {colorsArray.map((c, i) => {
                        const colorName = typeof c === 'string' ? c : c.name;
                        return (
                          <option key={i} value={colorName}>{colorName}</option>
                        );
                      })}
                    </select>

                    {selectedColorForImages && (
                      <>
                        {/* Option 1: Upload */}
                        <div className="mb-3">
                          <label className="block text-xs font-semibold text-gray-600 mb-2">Option 1 : Upload d'image</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e, selectedColorForImages)}
                              disabled={uploadingImage}
                              className="flex-1 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#00aaff] file:text-white hover:file:bg-[#0088dd] file:cursor-pointer"
                            />
                            {uploadingImage && (
                              <div className="w-6 h-6 border-2 border-[#00aaff] border-t-transparent rounded-full animate-spin"></div>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Max 5MB - JPG, PNG, WebP</p>
                        </div>

                        {/* Option 2: URL */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-2">Option 2 : URL d'image</label>
                          <div className="flex gap-2">
                            <input
                              value={newImageUrl}
                              onChange={e => setNewImageUrl(e.target.value)}
                              placeholder="https://example.com/image.jpg"
                              className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                            />
                            <button
                              type="button"
                              onClick={addImageToColor}
                              disabled={!newImageUrl}
                              className="bg-[#00aaff] text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#0088dd] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Display images by color */}
                  <div className="space-y-4">
                    {colorsArray.map((color, idx) => {
                      const colorName = typeof color === 'string' ? color : color.name;
                      const images = (form.color_images || {})[colorName] || [];
                      return (
                        <div key={idx} className="border border-gray-200 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            {typeof color === 'object' && color.value && (
                              <div
                                className="w-6 h-6 rounded border border-gray-200"
                                style={{ backgroundColor: color.value }}
                              />
                            )}
                            <p className="font-semibold text-gray-900">{colorName}</p>
                          </div>
                          {images.length === 0 ? (
                            <p className="text-sm text-gray-400">Aucune image pour cette couleur</p>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {images.map((url, i) => (
                                <div 
                                  key={i} 
                                  draggable
                                  onDragStart={(e) => handleColorImageDragStart(e, colorName, i)}
                                  onDragOver={(e) => handleColorImageDragOver(e, i)}
                                  onDrop={(e) => handleColorImageDrop(e, colorName, i)}
                                  onDragEnd={handleColorImageDragEnd}
                                  className={`relative group cursor-move transition-all ${
                                    draggedColorImageIndex === i && draggedColorName === colorName
                                      ? 'opacity-50 scale-95' 
                                      : dragOverColorImageIndex === i && draggedColorName === colorName
                                      ? 'scale-105 ring-2 ring-[#00aaff]'
                                      : ''
                                  }`}
                                >
                                  <img src={url} alt={`${colorName} ${i+1}`} className="w-20 h-20 object-cover rounded-lg border-2 border-gray-200 pointer-events-none" />
                                  <button
                                    type="button"
                                    onClick={() => removeImageFromColor(colorName, i)}
                                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                  >
                                    <X size={12} />
                                  </button>
                                  {/* Drag indicator */}
                                  <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="bg-white/90 rounded px-1.5 py-0.5">
                                      <p className="text-[10px] font-bold text-gray-600">⋮⋮</p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Lens Config Tab */}
          {activeTab === 'lenses' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900">Configuration des verres</p>
                  <p className="text-sm text-gray-500">Activez cette option pour les lunettes optiques</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.has_lens_config || false}
                    onChange={e => setForm({...form, has_lens_config: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00aaff]"></div>
                </label>
              </div>

              {form.has_lens_config && (
                <>
                  {/* Add new lens option */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs font-semibold text-gray-500 mb-3 uppercase">Ajouter une option de verres</p>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          value={newLensOption.name}
                          onChange={e => setNewLensOption({...newLensOption, name: e.target.value})}
                          placeholder="Nom (ex: Anti lumière bleue)"
                          className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                        />
                        <input
                          type="number"
                          value={newLensOption.price_modifier}
                          onChange={e => setNewLensOption({...newLensOption, price_modifier: e.target.value})}
                          placeholder="Supplément €"
                          className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                        />
                      </div>
                      <input
                        value={newLensOption.emoji || ''}
                        onChange={e => setNewLensOption({...newLensOption, emoji: e.target.value})}
                        placeholder="Emoji (ex: 👓 🕶️ 🌙 ☀️)"
                        maxLength={4}
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                      />
                      <input
                        value={newLensOption.description}
                        onChange={e => setNewLensOption({...newLensOption, description: e.target.value})}
                        placeholder="Description"
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                      />
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex flex-col gap-1.5">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newLensOption.is_reimbursable || false}
                              onChange={e => setNewLensOption({...newLensOption, is_reimbursable: e.target.checked})}
                              className="w-4 h-4 accent-[#00aaff]"
                            />
                            <span className="text-sm text-gray-600">100% remboursé (Mutuelle/CMU)</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newLensOption.hide_price_label || false}
                              onChange={e => setNewLensOption({...newLensOption, hide_price_label: e.target.checked})}
                              className="w-4 h-4 accent-[#00aaff]"
                            />
                            <span className="text-sm text-gray-600">Masquer l'étiquette de prix (ex: "Inclus")</span>
                          </label>
                        </div>
                        <button
                          type="button"
                          onClick={addLensOption}
                          className="bg-[#00aaff] text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-[#0088dd] flex items-center gap-2"
                        >
                          <Plus size={14} /> Ajouter
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* List of lens options */}
                  <div className="space-y-2">
                    {(form.lens_options || []).length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <p>Aucune option de verres configurée</p>
                      </div>
                    ) : (
                      (form.lens_options || []).map((opt, i) => {
                        const updateField = (field, value) => {
                          const updated = [...form.lens_options];
                          updated[i] = { ...opt, [field]: value };
                          setForm({ ...form, lens_options: updated });
                        };
                        return (
                          <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid={`lens-option-row-${i}`}>
                            <div className="flex items-start gap-3">
                              <input
                                type="text"
                                value={opt.emoji || ''}
                                maxLength={4}
                                onChange={e => updateField('emoji', e.target.value)}
                                placeholder="😎"
                                className="w-14 shrink-0 text-center text-xl border border-gray-200 rounded-lg py-2 outline-none focus:border-[#00aaff]"
                                title="Emoji de l'option"
                                data-testid={`lens-option-emoji-${i}`}
                              />
                              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                                <input
                                  type="text"
                                  value={opt.name || ''}
                                  onChange={e => updateField('name', e.target.value)}
                                  placeholder="Nom (ex: Anti lumière bleue)"
                                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#00aaff]"
                                  data-testid={`lens-option-name-${i}`}
                                />
                                <input
                                  type="number"
                                  step="0.01"
                                  value={opt.price_modifier ?? 0}
                                  onChange={e => updateField('price_modifier', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                                  placeholder="Supplément €"
                                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#00aaff]"
                                  data-testid={`lens-option-price-${i}`}
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => removeLensOption(i)}
                                className="w-9 h-9 bg-red-100 text-red-600 rounded-lg flex items-center justify-center hover:bg-red-200 shrink-0"
                                title="Supprimer"
                                data-testid={`lens-option-delete-${i}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                            <input
                              type="text"
                              value={opt.description || ''}
                              onChange={e => updateField('description', e.target.value)}
                              placeholder="Description"
                              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#00aaff]"
                              data-testid={`lens-option-description-${i}`}
                            />
                            <div className="flex items-center justify-between">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={!!opt.is_reimbursable}
                                  onChange={e => updateField('is_reimbursable', e.target.checked)}
                                  className="w-4 h-4 accent-[#00aaff]"
                                  data-testid={`lens-option-reimbursable-${i}`}
                                />
                                <span className="text-sm text-gray-600">100% remboursé (Mutuelle/CMU)</span>
                              </label>
                              {opt.hide_price_label ? (
                                <span className="text-xs text-gray-400 italic">Étiquette masquée</span>
                              ) : (
                                <span className={`text-sm font-bold ${Number(opt.price_modifier) > 0 ? 'text-[#00aaff]' : 'text-green-600'}`}>
                                  {Number(opt.price_modifier) > 0 ? `+${Number(opt.price_modifier)}€` : 'Inclus'}
                                </span>
                              )}
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!opt.hide_price_label}
                                onChange={e => updateField('hide_price_label', e.target.checked)}
                                className="w-4 h-4 accent-[#00aaff]"
                                data-testid={`lens-option-hide-label-${i}`}
                              />
                              <span className="text-sm text-gray-600">Masquer l'étiquette de prix (ex: "Inclus")</span>
                            </label>
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* SEO Tab */}
          {activeTab === 'seo' && (
            <SEOEditor
              metaTitle={form.meta_title}
              metaDescription={form.meta_description}
              slug={form.slug}
              ogImage={form.og_image}
              defaultTitle={form.name}
              defaultDescription={form.description}
              isProductContext={true}
              onChange={(seoData) => setForm({...form, ...seoData})}
            />
          )}

          {/* Submit buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-100">
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-xl font-semibold hover:bg-gray-50">
              Annuler
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-[#00aaff] text-white py-3 rounded-xl font-semibold hover:bg-[#0088dd] flex items-center justify-center gap-2 disabled:opacity-60">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={16} />}
              {product?.id ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// User Form Modal
function UserModal({ user, onClose, onSave }) {
  // Split name into firstName and lastName if editing
  const nameParts = user?.name ? user.name.split(' ') : [];
  const initialFirstName = nameParts[0] || '';
  const initialLastName = nameParts.slice(1).join(' ') || '';

  const [form, setForm] = useState(user ? {
    firstName: initialFirstName,
    lastName: initialLastName,
    email: user.email || '',
    phone: user.phone || '',
    company: user.company || '',
    role: user.role || 'user',
    password: ''
  } : {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    role: 'user',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fullName = `${form.firstName} ${form.lastName}`.trim();
      
      if (user?.id) {
        // Update existing user
        await apiClient.put(`/admin/users/${user.id}`, {
          name: fullName,
          email: form.email,
          phone: form.phone || null,
          company: form.company || null,
          role: form.role
        });
        toast.success('Utilisateur mis à jour');
      } else {
        // Create new user
        if (!form.password || form.password.length < 6) {
          toast.error('Le mot de passe doit contenir au moins 6 caractères');
          setSaving(false);
          return;
        }
        await apiClient.post('/admin/users', {
          name: fullName,
          email: form.email,
          phone: form.phone || null,
          company: form.company || null,
          role: form.role,
          password: form.password
        });
        toast.success('Utilisateur créé');
      }
      onSave();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    try {
      await apiClient.put(`/admin/users/${user.id}/password`, { new_password: newPassword });
      toast.success('Mot de passe mis à jour');
      setChangingPassword(false);
      setNewPassword('');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleResetPassword = async () => {
    if (!window.confirm('Générer un nouveau mot de passe aléatoire et l\'envoyer par email à cet utilisateur ?')) return;
    try {
      const { data } = await apiClient.post(`/admin/users/${user.id}/reset-password`);
      if (data.new_password) {
        // Mailjet not configured, show password to admin
        toast.success(`Mot de passe réinitialisé : ${data.new_password}`, { duration: 10000 });
        prompt('Nouveau mot de passe (à transmettre manuellement) :', data.new_password);
      } else {
        toast.success(data.message);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-gray-900">{user?.id ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Prénom *</label>
              <input value={form.firstName} onChange={e => setForm({...form, firstName: e.target.value})} required
                placeholder="Jean"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Nom *</label>
              <input value={form.lastName} onChange={e => setForm({...form, lastName: e.target.value})} required
                placeholder="Dupont"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Email *</label>
            <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required
              placeholder="email@exemple.com"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Téléphone</label>
            <input value={form.phone || ''} onChange={e => setForm({...form, phone: e.target.value})}
              placeholder="+33 6 00 00 00 00"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Société</label>
            <input value={form.company || ''} onChange={e => setForm({...form, company: e.target.value})}
              placeholder="Nom de la société"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Rôle *</label>
            <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]">
              {ROLES.map(r => (
                <option key={r} value={r}>{r === 'user' ? 'Client' : r === 'pro' ? 'Professionnel' : 'Administrateur'}</option>
              ))}
            </select>
          </div>
          
          {/* Password field for new users */}
          {!user?.id && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Mot de passe *</label>
              <div className="relative">
                <input 
                  type={showPassword ? 'text' : 'password'} 
                  value={form.password} 
                  onChange={e => setForm({...form, password: e.target.value})}
                  required
                  placeholder="Min. 6 caractères"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff] pr-10" 
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          )}
          
          {/* Change password for existing users */}
          {user?.id && (
            <div className="pt-2 border-t border-gray-100 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-500 uppercase">Gestion du mot de passe</p>
                <button 
                  type="button" 
                  onClick={handleResetPassword}
                  className="flex items-center gap-2 text-sm text-white bg-orange-500 hover:bg-orange-600 px-3 py-1.5 rounded-lg font-medium transition-colors"
                >
                  <RotateCcw size={14} /> Générer & Envoyer
                </button>
              </div>
              
              {!changingPassword ? (
                <button type="button" onClick={() => setChangingPassword(true)}
                  className="flex items-center gap-2 text-sm text-[#00aaff] hover:underline font-medium">
                  <Key size={14} /> Modifier manuellement le mot de passe
                </button>
              ) : (
                <div className="space-y-3">
                  <label className="block text-xs font-semibold text-gray-500 uppercase">Nouveau mot de passe</label>
                  <div className="flex gap-2">
                    <input 
                      type="text" 
                      value={newPassword} 
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder="Min. 6 caractères"
                      className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]" 
                    />
                    <button type="button" onClick={handleChangePassword}
                      className="bg-[#00aaff] text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#0088dd]">
                      Changer
                    </button>
                    <button type="button" onClick={() => { setChangingPassword(false); setNewPassword(''); }}
                      className="border border-gray-200 text-gray-500 px-3 py-2.5 rounded-xl text-sm hover:bg-gray-50">
                      <X size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Temp password info if exists */}
          {user?.temp_password && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
              <p className="text-xs text-yellow-700 font-semibold mb-1">Mot de passe temporaire</p>
              <p className="text-sm text-yellow-800 font-mono">{user.temp_password}</p>
              <p className="text-xs text-yellow-600 mt-1">À communiquer au client pour sa première connexion</p>
            </div>
          )}
          
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-xl font-semibold hover:bg-gray-50">
              Annuler
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-[#00aaff] text-white py-3 rounded-xl font-semibold hover:bg-[#0088dd] flex items-center justify-center gap-2 disabled:opacity-60">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={16} />}
              {user?.id ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </form>
      </div>


    </div>
  );
}

// Email Templates Tab Component
// Promo Codes Management Tab
function PromoCodesTab() {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);

  const fetchCodes = async () => {
    try {
      const { data } = await apiClient.get('/admin/promo-codes');
      setPromoCodes(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCodes(); }, []);

  const toggleCode = async (codeId) => {
    try {
      await apiClient.patch(`/admin/promo-codes/${codeId}/toggle`);
      toast.success('Statut mis à jour');
      fetchCodes();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteCode = async (codeId) => {
    if (!window.confirm('Supprimer ce code promo ?')) return;
    try {
      await apiClient.delete(`/admin/promo-codes/${codeId}`);
      toast.success('Code supprimé');
      fetchCodes();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#00aaff] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-gray-500">{promoCodes.length} code(s) promo</p>
        <button
          onClick={() => { setEditingCode(null); setShowModal(true); }}
          className="flex items-center gap-2 bg-[#00aaff] text-white px-4 py-2 rounded-xl font-semibold hover:bg-[#0088dd]"
        >
          <Plus size={16} /> Créer un code promo
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Code</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Valeur</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Min. Commande</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Utilisations</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Expiration</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Statut</th>
              <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {promoCodes.map(code => (
              <tr key={code.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-6 py-4 font-bold text-[#00aaff]">{code.code}</td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {code.discount_type === 'fixed' ? (
                    <span className="flex items-center gap-1"><Euro size={14} /> Fixe</span>
                  ) : (
                    <span className="flex items-center gap-1"><Percent size={14} /> Pourcentage</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                  {code.discount_type === 'fixed' ? `${code.discount}€` : `${code.discount}%`}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">{code.min_order_amount}€</td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {code.current_uses || 0} {code.max_uses > 0 && `/ ${code.max_uses}`}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {code.expires_at ? new Date(code.expires_at).toLocaleDateString('fr-FR') : 'Illimité'}
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => toggleCode(code.id)}
                    className={`text-xs font-bold px-3 py-1 rounded-full ${
                      code.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {code.is_active ? 'Actif' : 'Inactif'}
                  </button>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => { setEditingCode(code); setShowModal(true); }}
                      className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center hover:bg-blue-200"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => deleteCode(code.id)}
                      className="w-8 h-8 bg-red-100 text-red-600 rounded-lg flex items-center justify-center hover:bg-red-200"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <PromoCodeModal
          code={editingCode}
          onClose={() => { setShowModal(false); setEditingCode(null); }}
          onSave={() => { setShowModal(false); setEditingCode(null); fetchCodes(); }}
        />
      )}
    </div>
  );
}

// Promo Code Modal
function PromoCodeModal({ code, onClose, onSave }) {
  const [form, setForm] = useState(code || {
    code: '',
    discount_type: 'percentage',
    discount: '',
    description: '',
    min_order_amount: 0,
    max_uses: 0,
    expires_at: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...form,
        discount: parseFloat(form.discount),
        min_order_amount: parseFloat(form.min_order_amount),
        max_uses: parseInt(form.max_uses) || 0,
        expires_at: form.expires_at || null
      };
      
      if (code?.id) {
        await apiClient.put(`/admin/promo-codes/${code.id}`, data);
        toast.success('Code mis à jour');
      } else {
        await apiClient.post('/admin/promo-codes', data);
        toast.success('Code créé');
      }
      onSave();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full">
        <div className="border-b border-gray-100 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">{code ? 'Modifier' : 'Créer'} un code promo</h2>
          <button onClick={onClose} className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Code */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Code Promo</label>
            <input
              type="text"
              required
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="ex: SOLDES20"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff] uppercase"
            />
          </div>

          {/* Type and Value */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Type de remise</label>
              <select
                value={form.discount_type}
                onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
              >
                <option value="percentage">Pourcentage (%)</option>
                <option value="fixed">Montant fixe (€)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Valeur</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={form.discount}
                onChange={(e) => setForm({ ...form, discount: e.target.value })}
                placeholder={form.discount_type === 'fixed' ? '10.00' : '20'}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
              />
            </div>
          </div>

          {/* Min order and max uses */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Commande minimum (€)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.min_order_amount}
                onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Limite d'utilisation (0 = illimité)</label>
              <input
                type="number"
                min="0"
                value={form.max_uses}
                onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
              />
            </div>
          </div>

          {/* Expiration date */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Date d'expiration (optionnel)</label>
            <input
              type="date"
              value={form.expires_at ? form.expires_at.split('T')[0] : ''}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Description (optionnel)</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              placeholder="Description interne du code promo..."
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
            />
          </div>

          {/* Active status */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="w-4 h-4 text-[#00aaff] rounded"
            />
            <label htmlFor="is_active" className="text-sm text-gray-700 cursor-pointer">Code actif dès la création</label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-[#00aaff] text-white rounded-xl text-sm font-semibold hover:bg-[#0088dd] disabled:opacity-60 flex items-center gap-2"
            >
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={14} />}
              {code ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EmailTemplatesTab() {
  const [templates, setTemplates] = useState({});
  const [selectedKey, setSelectedKey] = useState(null);
  const [editedHtml, setEditedHtml] = useState('');
  const [editedSubject, setEditedSubject] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCode, setShowCode] = useState(true);
  const [logoUrl, setLogoUrl] = useState('');
  const [logoInput, setLogoInput] = useState('');
  const [savingLogo, setSavingLogo] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchEmailBranding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEmailBranding = async () => {
    try {
      const { data } = await apiClient.get('/admin/settings/email-branding');
      setLogoUrl(data.logo_url || '');
      setLogoInput(data.logo_url || '');
    } catch (err) {
      console.error('Error fetching email branding:', err);
    }
  };

  const handleSaveLogo = async () => {
    if (!logoInput) return;
    setSavingLogo(true);
    try {
      await apiClient.put('/admin/settings/email-branding', { logo_url: logoInput });
      setLogoUrl(logoInput);
      toast.success('Logo email mis à jour pour tous les templates');
      // Refresh preview
      if (selectedKey) {
        try {
          const previewRes = await apiClient.post(`/admin/email-templates/${selectedKey}/preview`);
          setPreviewHtml(previewRes.data.html);
        } catch (previewErr) { console.error("Preview refresh failed:", previewErr); }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingLogo(false);
    }
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Le fichier doit être une image');
      return;
    }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await apiClient.post('/admin/settings/email-branding/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const fullUrl = data.logo_url.startsWith('/api') 
        ? window.location.origin + data.logo_url 
        : data.logo_url;
      setLogoUrl(data.logo_url);
      setLogoInput(data.logo_url);
      toast.success('Logo uploadé et appliqué à tous les emails');
      if (selectedKey) {
        try {
          const previewRes = await apiClient.post(`/admin/email-templates/${selectedKey}/preview`);
          setPreviewHtml(previewRes.data.html);
        } catch (previewErr) { console.error("Preview refresh failed:", previewErr); }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingLogo(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const { data } = await apiClient.get('/admin/email-templates');
      setTemplates(data);
      // Select first template by default
      const keys = Object.keys(data);
      if (keys.length > 0 && !selectedKey) {
        selectTemplate(keys[0], data[keys[0]]);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectTemplate = async (key, template) => {
    setSelectedKey(key);
    setEditedHtml(template.html);
    setEditedSubject(template.subject);
    // Get preview
    try {
      const { data } = await apiClient.post(`/admin/email-templates/${key}/preview`);
      setPreviewHtml(data.html);
    } catch {
      setPreviewHtml(template.html);
    }
  };

  const handleSave = async () => {
    if (!selectedKey) return;
    setSaving(true);
    try {
      await apiClient.put(`/admin/email-templates/${selectedKey}`, {
        subject: editedSubject,
        html: editedHtml
      });
      toast.success('Template sauvegardé');
      // Refresh
      const { data } = await apiClient.get(`/admin/email-templates/${selectedKey}`);
      setTemplates(prev => ({ ...prev, [selectedKey]: data }));
      // Update preview
      const previewRes = await apiClient.post(`/admin/email-templates/${selectedKey}/preview`);
      setPreviewHtml(previewRes.data.html);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedKey) return;
    if (!window.confirm('Réinitialiser ce template à sa version par défaut ?')) return;
    try {
      await apiClient.delete(`/admin/email-templates/${selectedKey}`);
      toast.success('Template réinitialisé');
      fetchTemplates();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updatePreview = async () => {
    if (!selectedKey) return;
    // Simple client-side preview with sample data
    const sampleData = {
      logo_url: logoUrl || "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/697f50f37c0216a7af95ca66/9aa466458_Sanstitre.png",
      customer_name: "Jean Dupont",
      customer_email: "jean.dupont@example.com",
      order_number: "JC-20260401-ABC123",
      order_total: "349.00",
      order_items: "1x NEWFARER Bleu Dégradé - 349€",
      shipping_address: "12 Rue de la Paix<br>75001 Paris, France",
      tracking_number: "1Z999AA10123456784",
      tracking_url: "https://track.example.com/1Z999AA10123456784",
      carrier: "Chronopost",
      contact_name: "Marie Martin",
      company_name: "Optique Martin SARL",
      email: "contact@optiquemartin.fr",
      temp_password: "(exemple-mdp-temporaire)",
      cart_items: "1x LYRA Optique - 299€<br>1x VOLT Caméra - 499€",
      cart_total: "798.00",
      promo_code: "WELCOME10",
      status_label: "Expédiée",
      order_date: "2026-04-01",
      items_html: '<tr><td style="padding:10px;border-bottom:1px solid #eee;">NEWFARER Bleu Dégradé</td><td style="padding:10px;border-bottom:1px solid #eee;text-align:center;">1</td><td style="padding:10px;border-bottom:1px solid #eee;text-align:right;">349€</td></tr>',
      total_ttc: "349.00",
      tracking_html: '<div class="tracking-box"><h3>Suivi de livraison</h3><p>Numéro: 1Z999AA10123456784</p></div>',
      custom_message_html: '<div class="custom-msg"><p>Votre colis sera livré demain entre 9h et 12h.</p></div>',
      payment_url: "https://joeycyan.com/payer/exemple-token",
      user_name: "Jean Dupont",
      new_password: "(exemple-nouveau-mdp)",
      message: "Ceci est un email de test. Votre configuration Mailjet fonctionne correctement.",
      refund_amount: "349.00",
      refund_items: "1x NEWFARER Bleu Dégradé",
      refund_reason: "Produit défectueux",
      iban: "FR76 1234 5678 9012 3456 7890 123",
      bic: "BNPAFRPPXXX",
      bank_name: "BNP Paribas",
      account_holder: "Joey Cyan SAS",
      amount_due: "349.00"
    };
    let html = editedHtml;
    for (const [key, value] of Object.entries(sampleData)) {
      html = html.replace(new RegExp(`{{${key}}}`, 'g'), value);
    }
    setPreviewHtml(html);
  };

  useEffect(() => {
    if (editedHtml) {
      const timer = setTimeout(updatePreview, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedHtml]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#00aaff] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const templatesList = Object.entries(templates);
  const currentTemplate = selectedKey ? templates[selectedKey] : null;

  return (
    <div className="space-y-6">
      {/* Logo Email - Global Setting */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="email-logo-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold text-gray-900 text-lg">Logo des emails</h3>
            <p className="text-sm text-gray-500">Ce logo s'affiche dans le header de tous vos emails. Modifiez-le ici, il sera appliqué partout.</p>
          </div>
          {logoUrl && (
            <div className="flex-shrink-0 bg-gradient-to-r from-[#00aaff] to-[#0088dd] rounded-xl p-4">
              <img 
                src={logoUrl} 
                alt="Logo email actuel" 
                className="h-10 max-w-[160px] object-contain"
                data-testid="email-logo-preview"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
        </div>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">URL du logo</label>
            <input
              type="text"
              value={logoInput}
              onChange={(e) => setLogoInput(e.target.value)}
              placeholder="https://example.com/logo.png"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00aaff]/20 focus:border-[#00aaff]"
              data-testid="email-logo-url-input"
            />
          </div>
          <button
            onClick={handleSaveLogo}
            disabled={savingLogo || logoInput === logoUrl}
            className="px-5 py-2.5 bg-[#00aaff] text-white rounded-xl text-sm font-semibold hover:bg-[#0088dd] disabled:opacity-40 flex items-center gap-2 whitespace-nowrap"
            data-testid="email-logo-save-btn"
          >
            {savingLogo ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={14} />}
            Appliquer
          </button>
          <label className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-200 cursor-pointer flex items-center gap-2 whitespace-nowrap">
            {uploadingLogo ? <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> : <Upload size={14} />}
            Uploader
            <input type="file" accept="image/*" className="hidden" onChange={handleUploadLogo} data-testid="email-logo-upload-input" />
          </label>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-12 gap-6">
      {/* Sidebar - Template List */}
      <div className="col-span-12 md:col-span-3">
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-900">Templates</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {templatesList.map(([key, template]) => (
              <button
                key={key}
                onClick={() => selectTemplate(key, template)}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                  selectedKey === key ? 'bg-[#e0f4ff] border-l-4 border-[#00aaff]' : ''
                }`}
              >
                <p className="font-semibold text-gray-900 text-sm">{template.name}</p>
                <p className="text-xs text-gray-400 mt-0.5">{template.description}</p>
                {template.is_customized && (
                  <span className="inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                    Personnalisé
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content - Editor & Preview */}
      <div className="col-span-12 md:col-span-9">
        {currentTemplate ? (
          <div className="space-y-4">
            {/* Header */}
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{currentTemplate.name}</h2>
                  <p className="text-sm text-gray-500">{currentTemplate.description}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleReset}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50"
                  >
                    <RotateCcw size={14} /> Réinitialiser
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-[#00aaff] text-white rounded-xl text-sm font-semibold hover:bg-[#0088dd] disabled:opacity-60"
                  >
                    {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={14} />}
                    Sauvegarder
                  </button>
                </div>
              </div>
            </div>

            {/* Subject */}
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Sujet de l'email</label>
              <input
                value={editedSubject}
                onChange={(e) => setEditedSubject(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
              />
            </div>

            {/* Variables */}
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <p className="text-xs font-semibold text-gray-500 mb-2 uppercase">Variables disponibles</p>
              <div className="flex flex-wrap gap-2">
                {currentTemplate.variables.map(v => (
                  <code key={v} className="text-xs bg-gray-100 text-[#00aaff] px-2 py-1 rounded-lg font-mono">
                    {`{{${v}}}`}
                  </code>
                ))}
              </div>
            </div>

            {/* Editor / Preview Toggle */}
            <div className="flex gap-2 mb-2">
              <button
                onClick={() => setShowCode(true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  showCode ? 'bg-[#00aaff] text-white' : 'bg-white border border-gray-200 text-gray-600'
                }`}
              >
                <Code size={14} /> Code HTML
              </button>
              <button
                onClick={() => setShowCode(false)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  !showCode ? 'bg-[#00aaff] text-white' : 'bg-white border border-gray-200 text-gray-600'
                }`}
              >
                <Eye size={14} /> Aperçu
              </button>
            </div>

            {/* Editor or Preview */}
            {showCode ? (
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <textarea
                  value={editedHtml}
                  onChange={(e) => setEditedHtml(e.target.value)}
                  className="w-full h-[500px] p-4 font-mono text-sm outline-none resize-none bg-gray-900 text-green-400"
                  spellCheck={false}
                />
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <iframe
                  srcDoc={previewHtml}
                  title="Email Preview"
                  className="w-full h-[500px] border-0"
                  sandbox="allow-same-origin"
                />
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
            <Mail size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">Sélectionnez un template à modifier</p>
          </div>
        )}
      </div>
    </div>
    </div>
  );
}

// Order Details Modal for WooCommerce-style management
function OrderDetailsModal({ order: initialOrder, onClose, onUpdate }) {
  const [order, setOrder] = useState(initialOrder); // Local state for live updates
  
  // Parse customer name
  const nameParts = (initialOrder?.customer_name || '').split(' ');
  const initialFirstName = nameParts[0] || '';
  const initialLastName = nameParts.slice(1).join(' ') || '';
  
  const [form, setForm] = useState({
    customer_first_name: initialOrder?.customer_first_name || initialFirstName,
    customer_last_name: initialOrder?.customer_last_name || initialLastName,
    customer_company: initialOrder?.customer_company || '',
    customer_email: initialOrder?.customer_email || '',
    customer_phone: initialOrder?.customer_phone || '',
    shipping_address: initialOrder?.shipping_address || '',
    shipping_postal_code: initialOrder?.shipping_postal_code || '',
    shipping_city: initialOrder?.shipping_city || '',
    shipping_country: initialOrder?.shipping_country || 'France',
    use_different_billing: initialOrder?.use_different_billing || false,
    billing_first_name: initialOrder?.billing_first_name || '',
    billing_last_name: initialOrder?.billing_last_name || '',
    billing_company: initialOrder?.billing_company || '',
    billing_address: initialOrder?.billing_address || '',
    billing_postal_code: initialOrder?.billing_postal_code || '',
    billing_city: initialOrder?.billing_city || '',
    billing_country: initialOrder?.billing_country || 'France',
    status: initialOrder?.status || 'pending',
    tracking_number: initialOrder?.tracking_number || '',
    carrier: initialOrder?.carrier || '',
    notes: initialOrder?.notes || '',
  });
  const [emailType, setEmailType] = useState('shipped'); // 'shipped', 'custom'
  const [showMultiplePaymentModal, setShowMultiplePaymentModal] = useState(false);
  const [ribSentHistory, setRibSentHistory] = useState(initialOrder?.rib_sent_history || []);

  // Synchroniser le formulaire quand order est mis à jour (ex: après paiement)
  useEffect(() => {
    if (order) {
      setForm(prev => ({
        ...prev,
        status: order.status || prev.status,
        tracking_number: order.tracking_number || prev.tracking_number,
        carrier: order.carrier || prev.carrier,
        notes: order.notes !== undefined ? order.notes : prev.notes,
      }));
      setRibSentHistory(order.rib_sent_history || []);
    }
  }, [order]);
  const [showRibTemplate, setShowRibTemplate] = useState(false);
  const [ribTemplate, setRibTemplate] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Refund states
  const [showRefundSection, setShowRefundSection] = useState(false);
  const [refundType, setRefundType] = useState('partial_items'); // Default to item selection
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [selectedItems, setSelectedItems] = useState({}); // { itemIndex: { quantity, restock } }
  const [processing, setProcessing] = useState(false);

  // Calculate total refund amount based on selected items
  const calculateRefundAmount = () => {
    const availableForRefund = (order?.total_ttc || 0) - (order?.total_refunded || 0);
    
    let amount = 0;
    if (refundType === 'full') {
      amount = availableForRefund; // Only refund what's available, not full order
    } else if (refundType === 'shipping') {
      amount = Math.min(order?.shipping_cost_ttc || 0, availableForRefund);
    } else if (refundType === 'custom') {
      amount = Math.min(parseFloat(refundAmount) || 0, availableForRefund);
    } else if (refundType === 'partial_items') {
      Object.keys(selectedItems).forEach(itemIdx => {
        const item = order?.items?.[parseInt(itemIdx)];
        if (item && selectedItems[itemIdx].quantity > 0) {
          amount += item.price * selectedItems[itemIdx].quantity;
        }
      });
      amount = Math.min(amount, availableForRefund); // Cap at available amount
    }
    return Math.max(0, Math.min(amount, availableForRefund));
  };

  const handleUpdateOrder = async () => {
    setSaving(true);
    try {
      const customer_name = `${form.customer_first_name} ${form.customer_last_name}`.trim();
      const updateData = {
        ...form,
        customer_name
      };
      
      await apiClient.put(`/admin/orders/${order.id}`, updateData);
      toast.success('Commande mise à jour');
      
      // Reload the order to show changes immediately
      const { data: updatedOrder } = await apiClient.get(`/admin/orders/${order.id}`);
      setOrder(updatedOrder);
      
      await onUpdate();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      // Send notification email based on selected type
      await apiClient.post(`/admin/orders/${order.id}/notify`, {
        email_type: emailType,
        send_tracking: emailType === 'shipped',
        custom_message: emailType === 'custom' ? customMessage : undefined
      });
      
      toast.success('Email envoyé au client');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendRIB = async () => {
    try {
      const { data } = await apiClient.post(`/admin/orders/${order.id}/send-rib`);
      if (data.success) {
        toast.success(`✅ Email RIB envoyé à ${data.recipient}`);
      } else {
        toast.error(data.message || 'Erreur lors de l\'envoi');
      }
      setRibSentHistory(data.history || []);
      
      // Recharger la commande
      const { data: updatedOrder } = await apiClient.get(`/admin/orders/${order.id}`);
      setOrder(updatedOrder);
      await onUpdate();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleSendPaymentLink = async () => {
    try {
      const { data } = await apiClient.post(`/admin/orders/${order.id}/payment-link`);
      toast.success('Lien de paiement généré');
      // Copier le lien dans le presse-papiers
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(data.payment_url);
        toast.success('Lien copié dans le presse-papiers !');
      }
      alert(`Lien de paiement:\n\n${data.payment_url}\n\nLe lien a été copié dans le presse-papiers.`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleMarkPaidMultiple = async (paymentMethods) => {
    try {
      // Ajouter chaque paiement à l'historique via le nouveau endpoint
      for (const pm of paymentMethods) {
        await apiClient.post(`/admin/orders/${order.id}/add-payment`, {
          amount: pm.amount,
          method: pm.method,
          note: `Paiement enregistré manuellement - ${pm.method}`
        });
      }
      toast.success('Paiements enregistrés');
      const { data: updatedOrder } = await apiClient.get(`/admin/orders/${order.id}`);
      setOrder(updatedOrder);
      await onUpdate();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleRefund = async () => {
    if (!refundReason.trim()) {
      toast.error('Veuillez indiquer un motif de remboursement');
      return;
    }
    
    if (refundType === 'custom' && (!refundAmount || parseFloat(refundAmount) <= 0)) {
      toast.error('Veuillez saisir un montant valide');
      return;
    }
    
    if (refundType === 'partial_items' && Object.keys(selectedItems).length === 0) {
      toast.error('Veuillez sélectionner au moins un article à rembourser');
      return;
    }
    
    const totalRefund = calculateRefundAmount();
    if (totalRefund <= 0) {
      toast.error('Le montant du remboursement doit être supérieur à 0');
      return;
    }
    
    if (!window.confirm(`Confirmer le remboursement de ${totalRefund.toFixed(2)}€ ? Cette action est irréversible.`)) {
      return;
    }
    
    setProcessing(true);
    try {
      // Build items array for partial_items refund
      const items = refundType === 'partial_items' 
        ? Object.keys(selectedItems).map(itemIdx => {
            const item = order?.items?.[parseInt(itemIdx)];
            return {
              product_id: item?.product_id,
              quantity: selectedItems[itemIdx].quantity,
              restock: selectedItems[itemIdx].restock,
              color: item?.color || null,
              lens_option: item?.lens_option || null
            };
          })
        : undefined;
      
      const refundData = {
        refund_type: refundType,
        amount: refundType === 'custom' ? parseFloat(refundAmount) : undefined,
        items: items,
        reason: refundReason,
        notify_customer: notifyCustomer
      };
      
      const { data } = await apiClient.post(`/admin/orders/${order.id}/refund`, refundData);
      toast.success(data.message);
      
      // Reload the order to show new refund immediately
      const { data: updatedOrder } = await apiClient.get(`/admin/orders/${order.id}`);
      setOrder(updatedOrder); // Update local state
      
      // Reset form
      setShowRefundSection(false);
      setSelectedItems({});
      setRefundReason('');
      
      await onUpdate(); // Also update parent list
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelRefund = async (refundIndex) => {
    const refund = order.refunds[refundIndex];
    if (!window.confirm(`Annuler ce remboursement de ${refund.amount}€ ? Le stock sera inversé si nécessaire.`)) {
      return;
    }
    
    try {
      const { data } = await apiClient.delete(`/admin/orders/${order.id}/refunds/${refundIndex}`);
      toast.success(data.message);
      
      // Reload the order to get updated data
      const { data: updatedOrder } = await apiClient.get(`/admin/orders/${order.id}`);
      setOrder(updatedOrder); // Update local state
      
      await onUpdate(); // Also update parent list
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!order) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-4xl w-full my-8">
        {/* Header */}
        <div className="border-b border-gray-100 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Commande #{order.order_number}</h2>
            <p className="text-sm text-gray-500 mt-1">
              Créée le {new Date(order.created_at).toLocaleDateString('fr-FR')} à {order.created_time || new Date(order.created_at).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit', second: '2-digit'})}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Order Items Summary */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">Articles commandés</h3>
            <div className="space-y-2">
              {order.items?.map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {item.product_image && <img src={item.product_image} alt="" className="w-12 h-12 rounded-lg object-cover" />}
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{item.product_name}</p>
                      <p className="text-xs text-gray-500">Quantité: {item.quantity}</p>
                      {item.color && (
                        <p className="text-xs text-gray-500">
                          <span className="font-medium">Couleur:</span> {typeof item.color === 'object' ? item.color.name : item.color}
                        </p>
                      )}
                      {item.lens_option && (
                        <p className="text-xs text-gray-500">
                          <span className="font-medium">Verres:</span> {item.lens_option}
                        </p>
                      )}
                    </div>
                  </div>
                  <p className="text-sm font-bold text-[#00aaff]">{item.price}€</p>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between">
              <span className="font-bold text-gray-900">Total TTC</span>
              <span className="font-bold text-[#00aaff] text-lg">{order.total_ttc?.toFixed(2)}€</span>
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-blue-50 p-4 rounded-xl">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">Informations client</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Prénom</label>
                <input
                  type="text"
                  value={form.customer_first_name}
                  onChange={(e) => setForm({ ...form, customer_first_name: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Nom</label>
                <input
                  type="text"
                  value={form.customer_last_name}
                  onChange={(e) => setForm({ ...form, customer_last_name: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Société</label>
              <input
                type="text"
                value={form.customer_company}
                onChange={(e) => setForm({ ...form, customer_company: e.target.value })}
                placeholder="Optionnel"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Email</label>
                <input
                  type="email"
                  value={form.customer_email}
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Téléphone</label>
                <input
                  type="tel"
                  value={form.customer_phone}
                  onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                />
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="bg-purple-50 p-4 rounded-xl">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">Adresse de livraison</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Adresse</label>
                <input
                  type="text"
                  value={form.shipping_address}
                  onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Code postal</label>
                  <input
                    type="text"
                    value={form.shipping_postal_code}
                    onChange={(e) => setForm({ ...form, shipping_postal_code: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Ville</label>
                  <input
                    type="text"
                    value={form.shipping_city}
                    onChange={(e) => setForm({ ...form, shipping_city: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Pays</label>
                <input
                  type="text"
                  value={form.shipping_country}
                  onChange={(e) => setForm({ ...form, shipping_country: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                />
              </div>
            </div>
          </div>

          {/* Billing Address (if different) */}
          <div className="bg-orange-50 p-4 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700 uppercase">Adresse de facturation</h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.use_different_billing}
                  onChange={(e) => setForm({ ...form, use_different_billing: e.target.checked })}
                  className="w-4 h-4 accent-[#00aaff]"
                />
                <span className="text-sm text-gray-600">Différente de la livraison</span>
              </label>
            </div>
            
            {form.use_different_billing ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Prénom</label>
                    <input
                      type="text"
                      value={form.billing_first_name}
                      onChange={(e) => setForm({ ...form, billing_first_name: e.target.value })}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Nom</label>
                    <input
                      type="text"
                      value={form.billing_last_name}
                      onChange={(e) => setForm({ ...form, billing_last_name: e.target.value })}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Société</label>
                  <input
                    type="text"
                    value={form.billing_company}
                    onChange={(e) => setForm({ ...form, billing_company: e.target.value })}
                    placeholder="Optionnel"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Adresse</label>
                  <input
                    type="text"
                    value={form.billing_address}
                    onChange={(e) => setForm({ ...form, billing_address: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Code postal</label>
                    <input
                      type="text"
                      value={form.billing_postal_code}
                      onChange={(e) => setForm({ ...form, billing_postal_code: e.target.value })}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Ville</label>
                    <input
                      type="text"
                      value={form.billing_city}
                      onChange={(e) => setForm({ ...form, billing_city: e.target.value })}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Pays</label>
                  <input
                    type="text"
                    value={form.billing_country}
                    onChange={(e) => setForm({ ...form, billing_country: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                  />
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">Identique à l'adresse de livraison</p>
            )}
          </div>

          {/* Status and Tracking */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Statut</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
              >
                {STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{STATUS_LABELS[s].label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Transporteur</label>
              <input
                type="text"
                value={form.carrier}
                onChange={(e) => setForm({ ...form, carrier: e.target.value })}
                placeholder="ex: Colissimo, Chronopost..."
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">N° de suivi</label>
              <input
                type="text"
                value={form.tracking_number}
                onChange={(e) => setForm({ ...form, tracking_number: e.target.value })}
                placeholder="Numéro de tracking"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Notes internes (non visible par le client)</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              placeholder="Notes pour usage interne..."
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
            />
          </div>

          {/* Refund Section */}
          <div className="bg-red-50 rounded-xl p-4 border border-red-100">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <RotateCcw size={16} className="text-red-500" />
                  Remboursements
                </h3>
                {/* Available for refund (like WooCommerce) */}
                <p className="text-xs text-gray-600 mt-1">
                  Disponible pour remboursement : 
                  <span className="font-bold text-green-600 ml-1">
                    {((order.total_ttc || 0) - (order.total_refunded || 0)).toFixed(2)}€
                  </span>
                  <span className="text-gray-400 ml-1">
                    / {order.total_ttc || 0}€
                  </span>
                </p>
              </div>
              {/* Bouton remboursement - visible sauf si commande en attente de paiement */}
              {!showRefundSection && (order.total_ttc || 0) > (order.total_refunded || 0) && 
               !['pending', 'pending_payment'].includes(order.status) && (
                <button
                  onClick={() => setShowRefundSection(true)}
                  className="text-xs bg-red-500 text-white px-3 py-1.5 rounded-lg font-semibold hover:bg-red-600"
                >
                  + Nouveau remboursement
                </button>
              )}
              {/* Message si commande en attente de paiement */}
              {!showRefundSection && ['pending', 'pending_payment'].includes(order.status) && (
                <p className="text-xs text-gray-400 italic">
                  Remboursement disponible une fois payé
                </p>
              )}
            </div>

            {/* Existing refunds - Enhanced with item details */}
            {order.refunds && order.refunds.length > 0 && (
              <div className="mb-3 space-y-2">
                {order.refunds.map((refund, idx) => {
                  const isCancelled = refund.cancelled;
                  
                  return (
                    <div key={idx} className={`bg-white p-3 rounded-lg border ${isCancelled ? 'border-gray-300 opacity-60' : 'border-red-200'}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm font-bold ${isCancelled ? 'text-gray-400 line-through' : 'text-red-600'}`}>
                              {refund.amount}€ remboursé
                            </p>
                            {isCancelled && (
                              <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">ANNULÉ</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            Le {new Date(refund.created_at).toLocaleDateString('fr-FR')}
                            {isCancelled && refund.cancelled_at && (
                              <span className="ml-2">
                                (Annulé le {new Date(refund.cancelled_at).toLocaleDateString('fr-FR')})
                              </span>
                            )}
                          </p>
                          {refund.reason && <p className="text-xs text-gray-600 mt-1">Motif : {refund.reason}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">par {refund.created_by}</span>
                          {!isCancelled && (
                            <button
                              onClick={() => handleCancelRefund(idx)}
                              className="w-7 h-7 bg-red-100 text-red-600 rounded-lg flex items-center justify-center hover:bg-red-200"
                              title="Annuler ce remboursement"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Item details if available */}
                      {refund.items && refund.items.length > 0 && refund.items.some(i => i.product_name) && !isCancelled && (
                        <div className="mt-2 pt-2 border-t border-red-100">
                          <p className="text-xs font-semibold text-gray-500 mb-1">Articles remboursés :</p>
                          {refund.items.map((item, i) => item.product_name && (
                            <div key={i} className="flex items-center justify-between text-xs text-gray-600 py-1">
                              <div className="flex items-center gap-2">
                                <span>{item.product_name}</span>
                                {item.color && <span className="text-gray-400">({typeof item.color === 'object' ? item.color.name : item.color})</span>}
                                {item.lens_option && <span className="text-gray-400">[{item.lens_option}]</span>}
                                <span className="text-gray-400">x{item.quantity}</span>
                                {item.restock && <span className="text-green-600 font-semibold" title="Stock réintégré">↻</span>}
                              </div>
                              <span className="font-semibold">{item.total?.toFixed(2)}€</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="pt-2 border-t border-red-200">
                  <p className="text-sm font-bold text-gray-700">
                    Total remboursé : <span className="text-red-600">{order.total_refunded || 0}€</span> / {order.total_ttc}€
                  </p>
                </div>
              </div>
            )}

            {/* New refund form */}
            {showRefundSection && (
              <div className="space-y-3 bg-white p-4 rounded-lg border border-red-200">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-700">Créer un remboursement</p>
                  <button
                    onClick={() => setShowRefundSection(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                </div>

                {/* Refund type */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Type de remboursement</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="refundType"
                        value="partial_items"
                        checked={refundType === 'partial_items'}
                        onChange={(e) => setRefundType(e.target.value)}
                        className="w-4 h-4 text-red-500"
                      />
                      Sélectionner les articles à rembourser
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="refundType"
                        value="full"
                        checked={refundType === 'full'}
                        onChange={(e) => setRefundType(e.target.value)}
                        className="w-4 h-4 text-red-500"
                      />
                      Remboursement complet ({order.total_ttc}€)
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="refundType"
                        value="shipping"
                        checked={refundType === 'shipping'}
                        onChange={(e) => setRefundType(e.target.value)}
                        className="w-4 h-4 text-red-500"
                      />
                      Frais de livraison uniquement ({order.shipping_cost_ttc || 0}€)
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="refundType"
                        value="custom"
                        checked={refundType === 'custom'}
                        onChange={(e) => setRefundType(e.target.value)}
                        className="w-4 h-4 text-red-500"
                      />
                      Montant personnalisé
                    </label>
                  </div>
                </div>

                {/* Item selector for partial refund */}
                {refundType === 'partial_items' && (
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 mb-3 uppercase">Articles de la commande</p>
                    <div className="space-y-2">
                      {order.items?.map((item, idx) => {
                        const itemKey = String(idx);
                        const isSelected = !!selectedItems[itemKey];
                        const selectedQty = selectedItems[itemKey]?.quantity || 1;
                        const isRestocking = selectedItems[itemKey]?.restock || false;
                        const colorLabel = item.color ? (typeof item.color === 'object' ? item.color.name : item.color) : null;
                        
                        return (
                          <div key={idx} className={`bg-white p-3 rounded-lg border ${isSelected ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
                            <div className="flex items-start gap-3">
                              {/* Checkbox */}
                              <input
                                type="checkbox"
                                checked={isSelected}
                                data-testid={`refund-item-checkbox-${idx}`}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedItems({
                                      ...selectedItems,
                                      [itemKey]: { quantity: 1, restock: false }
                                    });
                                  } else {
                                    const newItems = { ...selectedItems };
                                    delete newItems[itemKey];
                                    setSelectedItems(newItems);
                                  }
                                }}
                                className="w-4 h-4 text-red-500 rounded mt-1"
                              />
                              
                              <div className="flex-1">
                                {/* Product info */}
                                <div className="flex items-start justify-between mb-2">
                                  <div>
                                    <p className="text-sm font-semibold text-gray-900">{item.product_name || item.name}</p>
                                    <p className="text-xs text-gray-500">
                                      Prix unitaire: {item.price}€ • Commandé: {item.quantity}
                                    </p>
                                    {colorLabel && (
                                      <p className="text-xs text-gray-500">
                                        <span className="font-medium">Couleur:</span> {colorLabel}
                                      </p>
                                    )}
                                    {item.lens_option && (
                                      <p className="text-xs text-gray-500">
                                        <span className="font-medium">Verres:</span> {item.lens_option}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Quantity and restock controls (only if selected) */}
                                {isSelected && (
                                  <div className="space-y-2 pt-2 border-t border-gray-200">
                                    <div className="flex items-center gap-3">
                                      <label className="text-xs font-semibold text-gray-500">Quantité à rembourser :</label>
                                      <input
                                        type="number"
                                        min="1"
                                        max={item.quantity}
                                        value={selectedQty}
                                        onChange={(e) => setSelectedItems({
                                          ...selectedItems,
                                          [itemKey]: {
                                            ...selectedItems[itemKey],
                                            quantity: Math.min(parseInt(e.target.value) || 1, item.quantity)
                                          }
                                        })}
                                        className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                                      />
                                      <span className="text-xs text-gray-600">
                                        = <span className="font-bold text-red-600">{(item.price * selectedQty).toFixed(2)}€</span>
                                      </span>
                                    </div>
                                    
                                    <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={isRestocking}
                                        onChange={(e) => setSelectedItems({
                                          ...selectedItems,
                                          [itemKey]: {
                                            ...selectedItems[itemKey],
                                            restock: e.target.checked
                                          }
                                        })}
                                        className="w-3 h-3 text-green-500 rounded"
                                      />
                                      <span className="flex items-center gap-1">
                                        Réintégrer au stock <span className="text-green-600">↻</span>
                                      </span>
                                    </label>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Total calculation */}
                    <div className="mt-3 pt-3 border-t border-gray-300 flex justify-between items-center">
                      <span className="text-sm font-semibold text-gray-700">Montant total du remboursement :</span>
                      <span className="text-lg font-bold text-red-600">{calculateRefundAmount().toFixed(2)}€</span>
                    </div>
                  </div>
                )}

                {/* Custom amount */}
                {refundType === 'custom' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Montant à rembourser (€)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      max={order.total_ttc - (order.total_refunded || 0)}
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-red-500"
                    />
                  </div>
                )}

                {/* Reason */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Motif du remboursement *</label>
                  <textarea
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    rows={2}
                    placeholder="Expliquez la raison du remboursement..."
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-red-500"
                  />
                </div>

                {/* Notify customer */}
                <div>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifyCustomer}
                      onChange={(e) => setNotifyCustomer(e.target.checked)}
                      className="w-4 h-4 text-red-500 rounded"
                    />
                    Envoyer un email de confirmation au client
                  </label>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => { setShowRefundSection(false); setSelectedItems({}); setRefundReason(''); }}
                    className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleRefund}
                    disabled={processing}
                    className="flex-1 px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {processing ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <RotateCcw size={14} />}
                    Effectuer le remboursement
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Payment Section - NEW */}
          <div className="bg-green-50 rounded-xl p-4 border border-green-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <CreditCard size={16} className="text-green-600" />
              Gestion des paiements
            </h3>

            {/* Payment History Section */}
            {(() => {
              const paymentHistory = order.payment_history || [];
              const totalPaid = paymentHistory
                .filter(p => p.status !== 'cancelled')
                .reduce((sum, p) => sum + (p.amount || 0), 0);
              const totalTtc = order.total_ttc || 0;
              const remaining = Math.max(0, totalTtc - totalPaid);
              const isFullyPaid = totalPaid >= totalTtc && totalPaid > 0;

              return (
                <>
                  {/* Payment Summary */}
                  <div className="mb-3 bg-white p-3 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-semibold text-gray-500">Total commande</span>
                      <span className="font-bold text-gray-800">{totalTtc.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-semibold text-gray-500">Total payé</span>
                      <span className={`font-bold ${isFullyPaid ? 'text-green-600' : totalPaid > 0 ? 'text-orange-500' : 'text-red-500'}`}>
                        {totalPaid.toFixed(2)}€
                      </span>
                    </div>
                    {remaining > 0 && (
                      <div className="flex justify-between items-center border-t border-gray-100 pt-2">
                        <span className="text-xs font-semibold text-red-500">Reste à payer</span>
                        <span className="font-bold text-red-500">{remaining.toFixed(2)}€</span>
                      </div>
                    )}
                    {isFullyPaid && (
                      <div className="border-t border-gray-100 pt-2 mt-1">
                        <p className="text-xs font-bold text-green-600 flex items-center gap-1">
                          <Check size={14} /> Entièrement payé
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Payment History List */}
                  {paymentHistory.length > 0 && (
                    <div className="mb-3 bg-white p-3 rounded-lg border border-green-200">
                      <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1">
                        <Clock size={12} /> Historique des paiements
                      </p>
                      {paymentHistory.map((payment, idx) => (
                        <div key={payment.id || idx} className={`text-xs py-2 border-b border-gray-50 last:border-0 ${payment.status === 'cancelled' ? 'opacity-50' : ''}`}>
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="font-semibold text-gray-700">{payment.amount?.toFixed(2)}€</span>
                              <span className="text-gray-400 ml-2">
                                {payment.method === 'stripe' ? 'Carte (Stripe)' : 
                                 payment.method === 'virement' ? 'Virement' :
                                 payment.method === 'especes' ? 'Espèces' :
                                 payment.method || 'Manuel'}
                              </span>
                              {payment.status === 'cancelled' && (
                                <span className="ml-2 text-red-500 font-bold">(Annulé)</span>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-gray-500">
                                {payment.date ? new Date(payment.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                              </div>
                              <div className="text-gray-400">
                                {payment.date ? new Date(payment.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
                              </div>
                            </div>
                          </div>
                          {payment.note && <p className="text-gray-400 mt-1">{payment.note}</p>}
                          {payment.cancelled_at && (
                            <p className="text-red-400 mt-1">Annulé le {new Date(payment.cancelled_at).toLocaleString('fr-FR')}</p>
                          )}
                          {payment.status !== 'cancelled' && (
                            <button
                              onClick={async () => {
                                if (!window.confirm('Êtes-vous sûr de vouloir annuler ce paiement ?')) return;
                                try {
                                  await apiClient.post(`/admin/orders/${order.id}/cancel-payment/${payment.id}`);
                                  toast.success('Paiement annulé');
                                  const { data: updatedOrder } = await apiClient.get(`/admin/orders/${order.id}`);
                                  setOrder(updatedOrder);
                                  await onUpdate();
                                } catch (err) {
                                  toast.error(formatApiError(err));
                                }
                              }}
                              className="mt-1 text-red-500 hover:text-red-700 font-semibold underline"
                            >
                              Annuler ce paiement
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Legacy payment_methods display */}
                  {order.payment_methods && order.payment_methods.length > 0 && !paymentHistory.length && (
                    <div className="mb-3 bg-white p-3 rounded-lg border border-green-200">
                      <p className="text-xs font-semibold text-gray-500 mb-2">Modes de paiement enregistrés :</p>
                      {order.payment_methods.map((pm, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm py-1">
                          <span className="text-gray-700">{pm.method}</span>
                          <span className="font-bold text-green-600">{pm.amount?.toFixed(2)}€</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}

            {/* RIB History */}
            {ribSentHistory.length > 0 && (
              <div className="mb-3 bg-white p-3 rounded-lg border border-blue-200">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs font-semibold text-gray-500">Historique envoi RIB :</p>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">
                    {ribSentHistory.length} envoi(s)
                  </span>
                </div>
                {ribSentHistory.map((entry, idx) => (
                  <div key={idx} className="text-xs text-gray-600 py-1 flex justify-between">
                    <span>{new Date(entry.date).toLocaleString('fr-FR')}</span>
                    <span className="text-gray-400">par {entry.sent_by}</span>
                  </div>
                ))}
                {showRibTemplate && ribTemplate && (
                  <button
                    onClick={() => setShowRibTemplate(!showRibTemplate)}
                    className="text-xs text-blue-600 hover:text-blue-700 underline mt-2"
                  >
                    {showRibTemplate ? 'Masquer' : 'Voir'} le template email
                  </button>
                )}
              </div>
            )}

            {/* Action buttons */}
            {(() => {
              const paymentHistory = order.payment_history || [];
              const totalPaid = paymentHistory
                .filter(p => p.status !== 'cancelled')
                .reduce((sum, p) => sum + (p.amount || 0), 0);
              const totalTtc = order.total_ttc || 0;
              const isFullyPaid = totalPaid >= totalTtc && totalPaid > 0;
              const hasPaid = totalPaid > 0;

              return (
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {/* Tant que pas entièrement payé → afficher les options de paiement */}
                  {!isFullyPaid && (
                    <>
                      <button
                        onClick={() => setShowMultiplePaymentModal(true)}
                        className="col-span-2 flex items-center justify-center gap-2 bg-green-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-green-700"
                        data-testid="mark-paid-btn"
                      >
                        <CreditCard size={14} /> Marquer comme payé
                      </button>
                      <button
                        onClick={handleSendPaymentLink}
                        className="flex items-center justify-center gap-2 bg-purple-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-purple-700"
                        data-testid="copy-payment-link-btn"
                      >
                        <Link2 size={14} /> Copier lien
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const { data } = await apiClient.post(`/admin/orders/${order.id}/send-payment-link-email`);
                            toast.success('Lien de paiement envoyé par email');
                          } catch (err) {
                            toast.error(formatApiError(err));
                          }
                        }}
                        className="flex items-center justify-center gap-2 bg-purple-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-purple-700"
                        data-testid="send-payment-link-btn"
                      >
                        <Mail size={14} /> Envoyer lien
                      </button>
                      <button
                        onClick={handleSendRIB}
                        className="col-span-2 flex items-center justify-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700"
                        data-testid="send-rib-btn"
                      >
                        <Mail size={14} /> Envoyer RIB par email
                      </button>
                    </>
                  )}
                  {isFullyPaid && (
                    <div className="col-span-2 bg-green-100 border border-green-300 rounded-lg p-3 text-center">
                      <p className="text-sm font-bold text-green-700 flex items-center justify-center gap-2">
                        <Check size={16} /> Commande entièrement payée
                      </p>
                    </div>
                  )}
                  
                  {/* Remboursement : seulement si au moins un paiement effectué */}
                  {hasPaid && (
                    <button
                      onClick={() => setShowRefundSection(!showRefundSection)}
                      className="col-span-2 flex items-center justify-center gap-2 bg-red-50 text-red-600 border border-red-200 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-red-100"
                      data-testid="refund-btn"
                    >
                      <RotateCcw size={14} /> {showRefundSection ? 'Masquer' : 'Rembourser'}
                    </button>
                  )}
                  {!hasPaid && (
                    <div className="col-span-2 bg-gray-50 border border-gray-200 rounded-lg p-2 text-center">
                      <p className="text-xs text-gray-400">Remboursement impossible - aucun paiement enregistré</p>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* RIB Template Preview */}
            {showRibTemplate && ribTemplate && (
              <div className="bg-white border border-gray-200 rounded-lg p-3 max-h-64 overflow-y-auto">
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(ribTemplate) }} />
              </div>
            )}
          </div>

          {/* Email Notification Section */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Mail size={16} className="text-[#00aaff]" />
              Notification Client (Email Mailjet)
            </h3>
            
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2">Type d'email</label>
                <select
                  value={emailType}
                  onChange={(e) => setEmailType(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff] bg-white"
                >
                  <option value="shipped">Récapitulatif de votre commande</option>
                  <option value="custom">Mise à jour personnalisée</option>
                </select>
              </div>

              {emailType === 'custom' && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2">Message personnalisé</label>
                  <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    rows={3}
                    placeholder="Ajoutez un message personnalisé pour le client..."
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
                  />
                </div>
              )}

              <button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="w-full flex items-center justify-center gap-2 bg-[#00aaff] text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#0088dd] disabled:opacity-60"
              >
                {sendingEmail ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Mail size={14} />}
                Envoyer l'email sélectionné
              </button>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-100 p-6 flex items-center justify-between bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-100"
          >
            Annuler
          </button>
          <button
            onClick={handleUpdateOrder}
            disabled={saving}
            className="px-4 py-2 bg-[#00aaff] text-white rounded-xl text-sm font-semibold hover:bg-[#0088dd] disabled:opacity-60 flex items-center gap-2"
          >
            {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={14} />}
            Sauvegarder les modifications
          </button>
        </div>
      </div>

      {/* Multiple Payment Modal */}
      {showMultiplePaymentModal && (
        <MultiplePaymentModal
          order={order}
          onClose={() => setShowMultiplePaymentModal(false)}
          onSave={handleMarkPaidMultiple}
        />
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [proRequests, setProRequests] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [users, setUsers] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState(null);
  const [showAttributeModal, setShowAttributeModal] = useState(false);

  // Manual Order Management States
  const [showManualOrderModal, setShowManualOrderModal] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null); // ID de la commande en cours d'édition
  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false);
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);
  const [manualOrderForm, setManualOrderForm] = useState({
    // Customer info
    customer_first_name: '',
    customer_last_name: '',
    customer_company: '',
    customer_email: '',
    customer_phone: '',
    // Shipping address
    shipping_address: '',
    shipping_postal_code: '',
    shipping_city: '',
    shipping_country: 'France',
    // Billing address
    use_different_billing: false,
    billing_first_name: '',
    billing_last_name: '',
    billing_company: '',
    billing_address: '',
    billing_postal_code: '',
    billing_city: '',
    billing_country: 'France',
    // Order details
    items: [],
    shipping_cost: 0,
    notes: ''
  });
  const [paymentForm, setPaymentForm] = useState({
    payment_source: 'virement',
    payment_notes: ''
  });

  // FAQ States
  const [faqCategories, setFaqCategories] = useState([]);
  const [faqItems, setFaqItems] = useState([]);
  const [editingFaqCategory, setEditingFaqCategory] = useState(null);
  const [editingFaqItem, setEditingFaqItem] = useState(null);
  const [showFaqCategoryModal, setShowFaqCategoryModal] = useState(false);
  const [showFaqItemModal, setShowFaqItemModal] = useState(false);

  // Fetch data on mount
  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes, productsRes, proRes, reviewsRes, usersRes, attributesRes, categoriesRes] = await Promise.all([
        apiClient.get('/admin/stats'),
        apiClient.get('/admin/orders'),
        apiClient.get('/admin/products'),
        apiClient.get('/admin/pro-requests'),
        apiClient.get('/admin/reviews/all'),
        apiClient.get('/admin/users'),
        apiClient.get('/admin/attributes'),
        apiClient.get('/admin/categories/all')
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data);
      setProducts(productsRes.data);
      setProRequests(proRes.data);
      setReviews(reviewsRes.data);
      setUsers(usersRes.data);
      setAttributes(attributesRes.data);
      setCategories(categoriesRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Fetch FAQ Data
  const fetchFaqData = async () => {
    try {
      const [categoriesRes, itemsRes] = await Promise.all([
        apiClient.get('/admin/faq-categories'),
        apiClient.get('/admin/faq-items')
      ]);
      setFaqCategories(categoriesRes.data);
      setFaqItems(itemsRes.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des FAQ');
    }
  };

  // Load FAQ when switching tabs
  useEffect(() => {
    if (activeTab === 'faq') {
      fetchFaqData();
    }
  }, [activeTab]);

  // Manual Order Functions
  const handleCreateManualOrder = async () => {
    try {
      setLoading(true);
      const customer_name = `${manualOrderForm.customer_first_name} ${manualOrderForm.customer_last_name}`.trim();
      
      const orderData = {
        customer_name,
        customer_first_name: manualOrderForm.customer_first_name,
        customer_last_name: manualOrderForm.customer_last_name,
        customer_company: manualOrderForm.customer_company,
        customer_email: manualOrderForm.customer_email,
        customer_phone: manualOrderForm.customer_phone,
        shipping_address: manualOrderForm.shipping_address,
        shipping_postal_code: manualOrderForm.shipping_postal_code,
        shipping_city: manualOrderForm.shipping_city,
        shipping_country: manualOrderForm.shipping_country,
        use_different_billing: manualOrderForm.use_different_billing,
        billing_first_name: manualOrderForm.billing_first_name,
        billing_last_name: manualOrderForm.billing_last_name,
        billing_company: manualOrderForm.billing_company,
        billing_address: manualOrderForm.billing_address,
        billing_postal_code: manualOrderForm.billing_postal_code,
        billing_city: manualOrderForm.billing_city,
        billing_country: manualOrderForm.billing_country,
        items: manualOrderForm.items.map(item => ({
          product_id: item.product_id,
          name: item.product_name,
          price: parseFloat(item.price),
          quantity: parseInt(item.quantity),
          color: item.color || null,
          lens_option: item.lens_option || null
        })),
        shipping_cost: parseFloat(manualOrderForm.shipping_cost) || 0
      };
      
      if (editingOrderId) {
        // Mode édition : PUT
        await apiClient.put(`/admin/orders/${editingOrderId}`, orderData);
        toast.success('Commande modifiée avec succès');
      } else {
        // Mode création : POST
        await apiClient.post('/admin/orders/manual', orderData);
        toast.success('Commande manuelle créée avec succès');
      }
      
      setShowManualOrderModal(false);
      setEditingOrderId(null);
      setManualOrderForm({
        customer_first_name: '',
        customer_last_name: '',
        customer_company: '',
        customer_email: '',
        customer_phone: '',
        shipping_address: '',
        shipping_postal_code: '',
        shipping_city: '',
        shipping_country: 'France',
        use_different_billing: false,
        billing_first_name: '',
        billing_last_name: '',
        billing_company: '',
        billing_address: '',
        billing_postal_code: '',
        billing_city: '',
        billing_country: 'France',
        items: [],
        shipping_cost: 0,
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSendPaymentEmail = async (orderId, emailType) => {
    try {
      setLoading(true);
      await apiClient.post(`/admin/orders/${orderId}/send-payment-link?email_type=${emailType}`);
      toast.success('Email envoyé avec succès');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async () => {
    try {
      setLoading(true);
      await apiClient.put(`/admin/orders/${selectedOrderForPayment}/mark-as-paid`, paymentForm);
      toast.success('Commande marquée comme payée');
      setShowMarkAsPaidModal(false);
      setSelectedOrderForPayment(null);
      setPaymentForm({ payment_source: 'virement', payment_notes: '' });
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  // Delete order
  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette commande ? Cette action est irréversible.")) {
      return;
    }

    try {
      await apiClient.delete(`/admin/orders/${orderId}`);
      toast.success('Commande supprimée avec succès');
      
      // Refresh orders list
      fetchData();
    } catch (err) {
      console.error('Delete order error:', err);
      toast.error(formatApiError(err));
    }
  };

  const addItemToManualOrder = (productId = null) => {
    if (productId) {
      // Add existing product

      const product = products.find(p => p.id === productId);
      if (product) {
        setManualOrderForm({
          ...manualOrderForm,
          items: [...manualOrderForm.items, {
            product_id: product.id,
            product_name: product.name,
            price: product.price,
            quantity: 1,
            color: product.colors && product.colors.length > 0 
              ? (typeof product.colors[0] === 'string' ? product.colors[0] : product.colors[0]?.name)
              : null,
            lens_option: null
          }]
        });
      }
    } else {
      // Add empty item for manual entry
      setManualOrderForm({
        ...manualOrderForm,
        items: [...manualOrderForm.items, { 
          product_id: null, 
          product_name: '', 
          price: 0, 
          quantity: 1,
          color: null,
          lens_option: null
        }]
      });
    }
  };

  const openEditOrderModal = (order) => {
    // Pré-remplir le formulaire avec les données de la commande existante
    setEditingOrderId(order.id);
    setManualOrderForm({
      customer_first_name: order.customer_name?.split(' ')[0] || '',
      customer_last_name: order.customer_name?.split(' ').slice(1).join(' ') || '',
      customer_company: order.customer_company || '',
      customer_email: order.customer_email || '',
      customer_phone: order.customer_phone || '',
      shipping_address: order.shipping_address?.split(',')[0]?.trim() || order.shipping_address || '',
      shipping_postal_code: order.shipping_address?.match(/\d{5}/)?.[0] || '',
      shipping_city: order.shipping_address?.split(',')[1]?.trim() || '',
      shipping_country: order.shipping_address?.split(',')[2]?.trim() || 'France',
      use_different_billing: false,
      billing_first_name: '',
      billing_last_name: '',
      billing_company: '',
      billing_address: '',
      billing_postal_code: '',
      billing_city: '',
      billing_country: 'France',
      items: order.items?.map(item => ({
        product_id: item.product_id || null,
        product_name: item.product_name || item.name || '',
        price: parseFloat(item.price) || 0,
        quantity: parseInt(item.quantity) || 1,
        color: item.color || null,
        lens_option: item.lens_option || item.lens_config || null
      })) || [],
      shipping_cost: parseFloat(order.shipping_cost_ttc) || 0
    });
    setShowManualOrderModal(true);
  };

  const removeItemFromManualOrder = (index) => {
    setManualOrderForm({
      ...manualOrderForm,
      items: manualOrderForm.items.filter((_, i) => i !== index)
    });
  };

  const updateManualOrderItem = (index, field, value) => {
    const newItems = [...manualOrderForm.items];
    
    if (field === 'product_id' && value) {
      // When product is selected, update all fields
      const product = products.find(p => p.id === value);
      if (product) {
        newItems[index] = {
          product_id: product.id,
          product_name: product.name,
          price: product.price,
          quantity: newItems[index].quantity || 1,
          color: product.colors && product.colors.length > 0 
            ? (typeof product.colors[0] === 'string' ? product.colors[0] : product.colors[0]?.name)
            : null,
          lens_option: null
        };
      }
    } else {
      newItems[index][field] = value;
    }
    
    setManualOrderForm({
      ...manualOrderForm,
      items: newItems
    });
  };

  const calculateManualOrderTotal = () => {
    const subtotal = manualOrderForm.items.reduce((sum, item) => 
      sum + (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1), 0
    );
    return subtotal + (parseFloat(manualOrderForm.shipping_cost) || 0);
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await apiClient.put(`/admin/orders/${orderId}/status?status=${status}`);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status } : o));
      toast.success('Statut mis à jour');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleProRequest = async (requestId, action) => {
    try {
      const { data } = await apiClient.put(`/admin/pro-requests/${requestId}/${action}`);
      setProRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: action === 'approve' ? 'approved' : 'rejected' } : r));
      toast.success(action === 'approve' ? 'Demande approuvée' : 'Demande rejetée');
      if (action === 'approve' && data.user_created) {
        toast.success('Compte utilisateur créé automatiquement');
        fetchData(); // Refresh to get new user
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleReviewStatus = async (reviewId, status) => {
    try {
      await apiClient.put(`/admin/reviews/${reviewId}/status?status=${status}`);
      setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, status } : r));
      toast.success('Avis mis à jour');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Supprimer cet avis ?')) return;
    try {
      await apiClient.delete(`/admin/reviews/${reviewId}`);
      setReviews(prev => prev.filter(r => r.id !== reviewId));
      toast.success('Avis supprimé');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try {
      await apiClient.delete(`/products/${productId}`);
      setProducts(prev => prev.filter(p => p.id !== productId));
      toast.success('Produit supprimé');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const duplicateProduct = async (productId) => {
    try {
      const { data } = await apiClient.post(`/products/${productId}/duplicate`);
      setProducts(prev => [data, ...prev]);
      toast.success('Produit dupliqué avec succès');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      await apiClient.delete(`/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('Utilisateur supprimé');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    try {
      await apiClient.delete(`/admin/categories/${categoryId}`);
      setCategories(prev => prev.filter(c => c._id !== categoryId));
      toast.success('Catégorie supprimée');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteAttribute = async (attributeId) => {
    if (!window.confirm('Supprimer cet attribut couleur ?')) return;
    try {
      await apiClient.delete(`/attributes/${attributeId}`);
      setAttributes(prev => prev.filter(a => a.id !== attributeId));
      toast.success('Attribut supprimé');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleAttributeSave = async (formData) => {
    try {
      if (editingAttribute) {
        await apiClient.put(`/attributes/${editingAttribute.id}`, formData);
        toast.success('Attribut modifié');
      } else {
        await apiClient.post('/attributes', formData);
        toast.success('Attribut créé');
      }
      setShowAttributeModal(false);
      setEditingAttribute(null);
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleCategorySave = async (formData) => {
    try {
      if (editingCategory) {
        await apiClient.put(`/admin/categories/${editingCategory._id}`, formData);
        toast.success('Catégorie modifiée');
      } else {
        await apiClient.post('/admin/categories', formData);
        toast.success('Catégorie créée');
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
      throw err;
    }
  };

  const handleProductSave = () => {
    setShowProductModal(false);
    setEditingProduct(null);
    fetchData();
  };

  const handleUserSave = () => {
    setShowUserModal(false);
    setEditingUser(null);
    fetchData();
  };

  // FAQ Functions
  const handleSaveFaqCategory = async (categoryData) => {
    try {
      if (editingFaqCategory) {
        await apiClient.put(`/admin/faq-categories/${editingFaqCategory.id}`, categoryData);
        toast.success('Catégorie mise à jour avec succès');
      } else {
        await apiClient.post('/admin/faq-categories', categoryData);
        toast.success('Catégorie créée avec succès');
      }
      setShowFaqCategoryModal(false);
      setEditingFaqCategory(null);
      fetchFaqData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDeleteFaqCategory = async (categoryId) => {
    if (!window.confirm('Êtes-vous sûr ? Toutes les questions de cette catégorie seront supprimées.')) return;
    try {
      await apiClient.delete(`/admin/faq-categories/${categoryId}`);
      toast.success('Catégorie supprimée');
      setShowFaqCategoryModal(false);
      setEditingFaqCategory(null);
      fetchFaqData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleSaveFaqItem = async (itemData) => {
    try {
      if (editingFaqItem) {
        await apiClient.put(`/admin/faq-items/${editingFaqItem.id}`, itemData);
        toast.success('Question mise à jour avec succès');
      } else {
        await apiClient.post('/admin/faq-items', itemData);
        toast.success('Question créée avec succès');
      }
      setShowFaqItemModal(false);
      setEditingFaqItem(null);
      fetchFaqData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDeleteFaqItem = async (itemId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette question ?')) return;
    try {
      await apiClient.delete(`/admin/faq-items/${itemId}`);
      toast.success('Question supprimée');
      setShowFaqItemModal(false);
      setEditingFaqItem(null);
      fetchFaqData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ========== SETTINGS TAB COMPONENT ==========


  // Contact Settings Form Component
  const ContactSettingsForm = () => {
    const [settings, setSettings] = useState({
      contact_email: '',
      contact_phone: '',
      whatsapp_number: '',
      notification_email: '',
      admin_email: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
      fetchContactSettings();
    }, []);

    const fetchContactSettings = async () => {
      try {
        const { data } = await apiClient.get('/admin/settings/contact');
        setSettings(data);
      } catch (err) {
        toast.error('Erreur lors du chargement des paramètres de contact');
      } finally {
        setLoading(false);
      }
    };

    const saveContactSettings = async () => {
      setSaving(true);
      try {
        await apiClient.put('/admin/settings/contact', settings);
        toast.success('Paramètres de contact sauvegardés !');
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setSaving(false);
      }
    };

    if (loading) {
      return <div className="text-center py-4">Chargement...</div>;
    }

    return (
      <div className="space-y-4">
        {/* Email Administrateur - Section mise en avant */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <label className="block text-xs font-semibold text-blue-700 mb-2 uppercase">
            Emails Administrateur (un ou plusieurs)
          </label>
          <input
            type="text"
            value={settings.admin_email || ''}
            onChange={(e) => setSettings({...settings, admin_email: e.target.value})}
            placeholder="admin@joeycyan.com, sav@joeycyan.com, benaimliroy@gmail.com"
            data-testid="admin-notification-emails"
            className="w-full border border-blue-300 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-500 bg-white"
          />
          <p className="text-xs text-blue-600 mt-2">
            Tous ces emails recevront les notifications : <strong>nouvelles commandes</strong>,
            <strong> demandes pro</strong>, <strong>messages de contact</strong> et
            <strong> nouveaux avis à modérer</strong>. Séparez les emails par une virgule.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
              Email de contact
            </label>
            <input
              type="email"
              value={settings.contact_email || ''}
              onChange={(e) => setSettings({...settings, contact_email: e.target.value})}
              placeholder="contact@joeycyan.com"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-green-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              Affiché dans le Footer et la page Contact
            </p>
          </div>
          
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
              Téléphone
            </label>
            <input
              type="tel"
              value={settings.contact_phone || ''}
              onChange={(e) => setSettings({...settings, contact_phone: e.target.value})}
              placeholder="+33 7 58 90 70 21"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-green-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              Format international recommandé
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
              Numéro WhatsApp
            </label>
            <input
              type="tel"
              value={settings.whatsapp_number || ''}
              onChange={(e) => setSettings({...settings, whatsapp_number: e.target.value})}
              placeholder="+33758907021"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-green-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              Sans espaces ni tirets (ex: +33758907021)
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
              Email de notification (formulaire)
            </label>
            <input
              type="email"
              value={settings.notification_email || ''}
              onChange={(e) => setSettings({...settings, notification_email: e.target.value})}
              placeholder="admin@joeycyan.com"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-green-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              Recevez les messages du formulaire de contact
            </p>
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <button
            onClick={saveContactSettings}
            disabled={saving}
            className="bg-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save size={18} />
                Enregistrer
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  // Social Media Settings Form Component
  const SocialMediaSettingsForm = () => {
    const [settings, setSettings] = useState({
      facebook_url: '',
      instagram_url: '',
      twitter_url: '',
      linkedin_url: '',
      youtube_url: '',
      tiktok_url: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
      fetchSocialSettings();
    }, []);

    const fetchSocialSettings = async () => {
      try {
        const { data } = await apiClient.get('/admin/settings/social');
        setSettings(data);
      } catch (err) {
        // Endpoint peut être absent pour l'instant — on garde les défauts
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Impossible de charger les paramètres sociaux:', err?.message || err);
        }
      } finally {
        setLoading(false);
      }
    };

    const saveSocialSettings = async () => {
      setSaving(true);
      try {
        await apiClient.put('/admin/settings/social', settings);
        toast.success('Réseaux sociaux sauvegardés !');
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setSaving(false);
      }
    };

    if (loading) {
      return <div className="text-center py-4">Chargement...</div>;
    }

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              Facebook
            </label>
            <input
              type="url"
              value={settings.facebook_url || ''}
              onChange={(e) => setSettings({...settings, facebook_url: e.target.value})}
              placeholder="https://facebook.com/joeycyan"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-purple-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              Instagram
            </label>
            <input
              type="url"
              value={settings.instagram_url || ''}
              onChange={(e) => setSettings({...settings, instagram_url: e.target.value})}
              placeholder="https://instagram.com/joeycyan"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-purple-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
              Twitter / X
            </label>
            <input
              type="url"
              value={settings.twitter_url || ''}
              onChange={(e) => setSettings({...settings, twitter_url: e.target.value})}
              placeholder="https://twitter.com/joeycyan"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-purple-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
              LinkedIn
            </label>
            <input
              type="url"
              value={settings.linkedin_url || ''}
              onChange={(e) => setSettings({...settings, linkedin_url: e.target.value})}
              placeholder="https://linkedin.com/company/joeycyan"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-purple-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              YouTube
            </label>
            <input
              type="url"
              value={settings.youtube_url || ''}
              onChange={(e) => setSettings({...settings, youtube_url: e.target.value})}
              placeholder="https://youtube.com/@joeycyan"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-purple-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
              TikTok
            </label>
            <input
              type="url"
              value={settings.tiktok_url || ''}
              onChange={(e) => setSettings({...settings, tiktok_url: e.target.value})}
              placeholder="https://tiktok.com/@joeycyan"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-purple-500"
            />
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <p className="text-xs text-blue-800">
            💡 <strong>Astuce :</strong> Laissez vide les réseaux que vous n'utilisez pas. Seuls les liens remplis apparaîtront dans le footer.
          </p>
        </div>

        <div className="flex justify-end pt-4">
          <button
            onClick={saveSocialSettings}
            disabled={saving}
            className="bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save size={18} />
                Enregistrer
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  const PackContentsForm = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
      apiClient.get('/settings/pack-contents').then(({ data }) => {
        setItems(data?.items || []);
      }).finally(() => setLoading(false));
    }, []);

    const save = async () => {
      setSaving(true);
      try {
        const cleaned = items.map(i => i.trim()).filter(Boolean);
        await apiClient.put('/settings/pack-contents', { items: cleaned });
        toast.success('Contenu du pack sauvegardé !');
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setSaving(false);
      }
    };

    if (loading) return <div className="text-center py-4">Chargement...</div>;

    return (
      <div className="space-y-3">
        <p className="text-xs text-gray-500">Liste affichée dans la section « 📦 Contenu du pack » sur chaque page produit.</p>
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              value={item}
              onChange={e => {
                const copy = [...items];
                copy[i] = e.target.value;
                setItems(copy);
              }}
              placeholder="Ex: Lunettes Joey Cyan"
              className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#00aaff]"
              data-testid={`pack-item-${i}`}
            />
            <button
              type="button"
              onClick={() => setItems(items.filter((_, idx) => idx !== i))}
              className="w-10 h-10 bg-red-100 text-red-600 rounded-lg flex items-center justify-center hover:bg-red-200"
              title="Supprimer"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setItems([...items, ''])}
          className="w-full border border-dashed border-gray-300 rounded-xl py-2.5 text-sm text-gray-500 hover:border-[#00aaff] hover:text-[#00aaff] flex items-center justify-center gap-2"
          data-testid="pack-add-item-btn"
        >
          <Plus size={14} /> Ajouter un élément
        </button>
        <button
          onClick={save}
          disabled={saving}
          className="bg-[#00aaff] text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#0088dd] disabled:opacity-50 flex items-center gap-2"
          data-testid="pack-save-btn"
        >
          {saving ? 'Enregistrement...' : <><Check size={16} /> Enregistrer</>}
        </button>
      </div>
    );
  };

  const SettingsTab = () => {
    const [settings, setSettings] = useState({
      stripe_test_secret_key: '',
      stripe_test_publishable_key: '',
      stripe_live_secret_key: '',
      stripe_live_publishable_key: '',
      stripe_mode: 'test',
      mailjet_api_key: '',
      mailjet_api_secret: '',
      mailjet_from_email: 'noreply@joeycyan.com',
      mailjet_from_name: 'Joey Cyan',
      gtm_id: '',
      returns_14_enabled: true,
      lens_config_enabled: true,
      bank_account_holder: '',
      bank_name: '',
      bank_iban: '',
      bank_bic: '',
      r2_account_id: '',
      r2_access_key_id: '',
      r2_secret_access_key: '',
      r2_bucket_name: '',
      r2_endpoint_url: '',
      r2_public_base_url: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [sendingTest, setSendingTest] = useState(false);
    const [testingR2, setTestingR2] = useState(false);
    const [testingTurnstile, setTestingTurnstile] = useState(false);

    useEffect(() => {
      fetchSettings();
    }, []);

    const fetchSettings = async () => {
      try {
        const { data } = await apiClient.get('/admin/settings');
        setSettings(data);
      } catch (err) {
        toast.error('Erreur lors du chargement des paramètres');
      } finally {
        setLoading(false);
      }
    };

    const saveSettings = async () => {
      setSaving(true);
      try {
        await apiClient.put('/admin/settings', settings);
        toast.success('Paramètres sauvegardés avec succès !');
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setSaving(false);
      }
    };

    const sendTestEmail = async () => {
      if (!testEmail) {
        toast.error('Veuillez entrer une adresse email');
        return;
      }

      setSendingTest(true);
      try {
        await apiClient.post('/admin/settings/test-email', {
          to_email: testEmail
        });
        toast.success(`Email de test envoyé à ${testEmail} !`);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setSendingTest(false);
      }
    };

    const testR2 = async () => {
      setTestingR2(true);
      try {
        const { data } = await apiClient.post('/admin/settings/test-r2');
        toast.success(`R2 OK — bucket: ${data.bucket}`);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setTestingR2(false);
      }
    };

    const testTurnstile = async () => {
      setTestingTurnstile(true);
      try {
        const { data } = await apiClient.post('/admin/settings/test-turnstile');
        toast.success(`Turnstile OK — site key: ${data.site_key}`);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setTestingTurnstile(false);
      }
    };

    if (loading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-[#00aaff] border-t-transparent rounded-full animate-spin"></div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Stripe Section */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-purple-100">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Key size={24} className="text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Stripe - Paiements</h2>
                <p className="text-sm text-gray-500">Configuration du paiement en ligne</p>
              </div>
            </div>
            
            {/* Mode Switch */}
            <div className="flex items-center gap-3 bg-gray-50 px-4 py-2 rounded-xl">
              <span className="text-sm font-semibold text-gray-600">Mode:</span>
              <button
                onClick={() => setSettings({...settings, stripe_mode: settings.stripe_mode === 'test' ? 'live' : 'test'})}
                className={`relative w-16 h-8 rounded-full transition-all ${
                  settings.stripe_mode === 'live' ? 'bg-green-500' : 'bg-amber-500'
                }`}
              >
                <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${
                  settings.stripe_mode === 'live' ? 'translate-x-8' : 'translate-x-0'
                }`} />
              </button>
              <span className={`text-sm font-bold ${settings.stripe_mode === 'live' ? 'text-green-600' : 'text-amber-600'}`}>
                {settings.stripe_mode === 'live' ? '🔴 LIVE' : '🧪 TEST'}
              </span>
            </div>
          </div>

          {/* Alert selon le mode */}
          <div className={`mb-4 p-3 rounded-xl border-2 ${
            settings.stripe_mode === 'live' 
              ? 'bg-red-50 border-red-200' 
              : 'bg-amber-50 border-amber-200'
          }`}>
            <p className={`text-sm font-semibold ${
              settings.stripe_mode === 'live' ? 'text-red-800' : 'text-amber-800'
            }`}>
              {settings.stripe_mode === 'live' 
                ? '🔴 Mode PRODUCTION : Les paiements seront réels et facturés aux clients !'
                : '🧪 Mode TEST : Les paiements ne seront pas réels (utilisez les cartes de test Stripe)'
              }
            </p>
          </div>

          {/* Test Mode Keys */}
          <div className={`mb-6 p-4 rounded-xl border-2 ${
            settings.stripe_mode === 'test' ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'
          }`}>
            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span>🧪 Clés de TEST</span>
              {settings.stripe_mode === 'test' && <span className="text-xs bg-amber-500 text-white px-2 py-1 rounded-full">ACTIF</span>}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                  Test Secret Key
                </label>
                <input
                  type="password"
                  value={settings.stripe_test_secret_key}
                  onChange={(e) => setSettings({...settings, stripe_test_secret_key: e.target.value})}
                  placeholder="sk_test_..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                  Test Publishable Key
                </label>
                <input
                  type="text"
                  value={settings.stripe_test_publishable_key}
                  onChange={(e) => setSettings({...settings, stripe_test_publishable_key: e.target.value})}
                  placeholder="pk_test_..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-500"
                />
              </div>
            </div>
          </div>

          {/* Live Mode Keys */}
          <div className={`p-4 rounded-xl border-2 ${
            settings.stripe_mode === 'live' ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
          }`}>
            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span>🔴 Clés de PRODUCTION</span>
              {settings.stripe_mode === 'live' && <span className="text-xs bg-green-500 text-white px-2 py-1 rounded-full">ACTIF</span>}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                  Live Secret Key
                </label>
                <input
                  type="password"
                  value={settings.stripe_live_secret_key}
                  onChange={(e) => setSettings({...settings, stripe_live_secret_key: e.target.value})}
                  placeholder="sk_live_..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                  Live Publishable Key
                </label>
                <input
                  type="text"
                  value={settings.stripe_live_publishable_key}
                  onChange={(e) => setSettings({...settings, stripe_live_publishable_key: e.target.value})}
                  placeholder="pk_live_..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-green-500"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-xs text-blue-800">
              💡 <strong>Conseil :</strong> Testez toujours en mode TEST avant de passer en PRODUCTION. Les cartes de test Stripe : 4242 4242 4242 4242 (toute date future, tout CVC)
            </p>
          </div>
        </div>

        {/* Mailjet Section */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <Mail size={24} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Mailjet</h2>
              <p className="text-sm text-gray-500">Configuration des emails transactionnels</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                API Key
              </label>
              <input
                type="password"
                value={settings.mailjet_api_key}
                onChange={(e) => setSettings({...settings, mailjet_api_key: e.target.value})}
                placeholder="Votre API Key Mailjet"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                API Secret
              </label>
              <input
                type="password"
                value={settings.mailjet_api_secret}
                onChange={(e) => setSettings({...settings, mailjet_api_secret: e.target.value})}
                placeholder="Votre API Secret Mailjet"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                Email expéditeur
              </label>
              <input
                type="email"
                value={settings.mailjet_from_email}
                onChange={(e) => setSettings({...settings, mailjet_from_email: e.target.value})}
                placeholder="noreply@joeycyan.com"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                Nom expéditeur
              </label>
              <input
                type="text"
                value={settings.mailjet_from_name}
                onChange={(e) => setSettings({...settings, mailjet_from_name: e.target.value})}
                placeholder="Joey Cyan"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-500"
              />
            </div>
          </div>

          {/* Test Email */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Tester l'envoi d'email</h3>
            <div className="flex gap-3">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="votre@email.com"
                className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-500"
              />
              <button
                onClick={sendTestEmail}
                disabled={sendingTest || !testEmail}
                className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {sendingTest ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Mail size={18} />
                )}
                {sendingTest ? 'Envoi...' : 'Envoyer un test'}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              💡 Envoyez un email de test pour vérifier que Mailjet fonctionne correctement
            </p>
          </div>
        </div>

        {/* Cloudflare R2 Section */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-cyan-100" data-testid="r2-settings-section">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center">
                <Database size={24} className="text-cyan-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Cloudflare R2 — Stockage médias</h2>
                <p className="text-sm text-gray-500">Images produits & avis sont stockées dans votre bucket R2</p>
              </div>
            </div>
            <button
              onClick={testR2}
              disabled={testingR2}
              data-testid="r2-test-btn"
              className="bg-cyan-600 text-white px-4 py-2 rounded-xl font-semibold hover:bg-cyan-700 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm"
            >
              {testingR2 ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Check size={16} />
              )}
              {testingR2 ? 'Test en cours…' : 'Tester la connexion'}
            </button>
          </div>

          <div className="bg-cyan-50 border border-cyan-200 rounded-lg px-4 py-3 mb-6 text-sm text-cyan-900">
            💡 Les champs secrets affichent <code className="font-mono">●●●●●●●●</code> une fois enregistrés. Laissez-les tels quels pour garder l'ancienne valeur, ou remplacez-les pour en saisir une nouvelle.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Account ID</label>
              <input
                type="text"
                value={settings.r2_account_id || ''}
                onChange={(e) => setSettings({ ...settings, r2_account_id: e.target.value })}
                placeholder="3ba5429c9817b63af153336c2dbc7bf0"
                data-testid="r2-account-id"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-cyan-500 font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Bucket Name</label>
              <input
                type="text"
                value={settings.r2_bucket_name || ''}
                onChange={(e) => setSettings({ ...settings, r2_bucket_name: e.target.value })}
                placeholder="joeycyan"
                data-testid="r2-bucket-name"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-cyan-500 font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Access Key ID</label>
              <input
                type="text"
                value={settings.r2_access_key_id || ''}
                onChange={(e) => setSettings({ ...settings, r2_access_key_id: e.target.value })}
                placeholder="••••••••"
                data-testid="r2-access-key-id"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-cyan-500 font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Secret Access Key</label>
              <input
                type="password"
                value={settings.r2_secret_access_key || ''}
                onChange={(e) => setSettings({ ...settings, r2_secret_access_key: e.target.value })}
                placeholder="••••••••"
                data-testid="r2-secret-access-key"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-cyan-500 font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Endpoint URL (S3 API)</label>
              <input
                type="text"
                value={settings.r2_endpoint_url || ''}
                onChange={(e) => setSettings({ ...settings, r2_endpoint_url: e.target.value })}
                placeholder="https://<account>.eu.r2.cloudflarestorage.com"
                data-testid="r2-endpoint-url"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-cyan-500 font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Public Base URL</label>
              <input
                type="text"
                value={settings.r2_public_base_url || ''}
                onChange={(e) => setSettings({ ...settings, r2_public_base_url: e.target.value })}
                placeholder="https://bucket.joeycyan.com"
                data-testid="r2-public-base-url"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-cyan-500 font-mono"
              />
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-4">
            ⚠️ Après modification, enregistrez avec « Sauvegarder » en bas de page, puis cliquez sur « Tester la connexion » pour valider.
          </p>
        </div>

        {/* Cloudflare Turnstile Section (anti-bot) */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-amber-100" data-testid="turnstile-settings-section">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Lock size={24} className="text-amber-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Cloudflare Turnstile — Anti-bot</h2>
                <p className="text-sm text-gray-500">Protège connexion, inscription, contact, avis, commandes invité…</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!settings.turnstile_enabled}
                  onChange={(e) => setSettings({ ...settings, turnstile_enabled: e.target.checked })}
                  data-testid="turnstile-enabled"
                  className="w-5 h-5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                Activé
              </label>
              <button
                onClick={testTurnstile}
                disabled={testingTurnstile}
                data-testid="turnstile-test-btn"
                className="bg-amber-600 text-white px-4 py-2 rounded-xl font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm"
              >
                {testingTurnstile ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Check size={16} />
                )}
                {testingTurnstile ? 'Test en cours…' : 'Tester la clé'}
              </button>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-6 text-sm text-amber-900">
            💡 Récupérez vos clés sur <a href="https://dash.cloudflare.com" target="_blank" rel="noreferrer" className="underline font-semibold">Cloudflare Dashboard</a> → Turnstile → votre site. Mode « Invisible » recommandé (aucun impact visuel sur vos formulaires).
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Site Key (publique)</label>
              <input
                type="text"
                value={settings.turnstile_site_key || ''}
                onChange={(e) => setSettings({ ...settings, turnstile_site_key: e.target.value })}
                placeholder="0x4AAAAAA…"
                data-testid="turnstile-site-key"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-amber-500 font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Secret Key</label>
              <input
                type="password"
                value={settings.turnstile_secret_key || ''}
                onChange={(e) => setSettings({ ...settings, turnstile_secret_key: e.target.value })}
                placeholder="••••••••"
                data-testid="turnstile-secret-key"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-amber-500 font-mono"
              />
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-4">
            ⚠️ Décocher « Activé » désactive la protection anti-bot sur tous les formulaires. À utiliser uniquement pour dépannage.
          </p>
        </div>

        {/* Google Tag Manager Section */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
              <Code size={24} className="text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Google Tag Manager</h2>
              <p className="text-sm text-gray-500">Gestion des balises et tracking</p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
              ID Google Tag Manager
            </label>
            <input
              type="text"
              value={settings.gtm_id || ''}
              onChange={(e) => setSettings({...settings, gtm_id: e.target.value})}
              placeholder="GTM-XXXXXXX"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-orange-500"
            />
            <p className="text-xs text-gray-400 mt-2">
              📌 Le code GTM sera automatiquement injecté dans le <code>&lt;head&gt;</code> et <code>&lt;body&gt;</code> de toutes les pages
            </p>
            <p className="text-xs text-gray-400 mt-1">
              🔗 <a href="https://tagmanager.google.com/" target="_blank" rel="noopener noreferrer" className="text-[#00aaff] hover:underline">
                Obtenir votre ID GTM sur Google Tag Manager
              </a>
            </p>
          </div>
        </div>

        {/* Product Display Options Section */}
        <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="display-options-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Eye size={24} className="text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Options d'affichage des fiches produit</h2>
              <p className="text-sm text-gray-500">Activer / désactiver les éléments affichés sur toutes les pages produit</p>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            <div className="py-4 first:pt-0">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-1">
                    Badge « Retours 14 jours »
                  </label>
                  <p className="text-xs text-gray-500">
                    Afficher le badge <code>✓ Retours 14 jours</code> sur les fiches produit.
                    Automatiquement masqué pour les comptes professionnels connectés.
                  </p>
                </div>
                <button
                  type="button"
                  data-testid="toggle-returns-14"
                  onClick={() => setSettings({...settings, returns_14_enabled: !(settings.returns_14_enabled !== false)})}
                  className={`shrink-0 mt-1 relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                    settings.returns_14_enabled !== false ? 'bg-[#00aaff]' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      settings.returns_14_enabled !== false ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <p className={`text-xs mt-2 font-semibold ${settings.returns_14_enabled !== false ? 'text-green-600' : 'text-gray-500'}`}>
                {settings.returns_14_enabled !== false ? '✓ Badge activé (visible pour les clients non-pro)' : '✕ Badge désactivé sur toutes les fiches produit'}
              </p>
            </div>

            <div className="py-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-1">
                    Bloc « Configuration des verres »
                  </label>
                  <p className="text-xs text-gray-500">
                    Afficher le sélecteur de verres (Standard / Correcteurs) sur les fiches produit concernées.
                    Automatiquement masqué pour les comptes professionnels connectés.
                  </p>
                </div>
                <button
                  type="button"
                  data-testid="toggle-lens-config"
                  onClick={() => setSettings({...settings, lens_config_enabled: !(settings.lens_config_enabled !== false)})}
                  className={`shrink-0 mt-1 relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                    settings.lens_config_enabled !== false ? 'bg-[#00aaff]' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      settings.lens_config_enabled !== false ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <p className={`text-xs mt-2 font-semibold ${settings.lens_config_enabled !== false ? 'text-green-600' : 'text-gray-500'}`}>
                {settings.lens_config_enabled !== false ? '✓ Bloc activé (visible pour les clients non-pro)' : '✕ Bloc désactivé sur toutes les fiches produit'}
              </p>
            </div>
          </div>
        </div>

        {/* RIB / Bank Information Section */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
              <Banknote size={24} className="text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Informations Bancaires (RIB)</h2>
              <p className="text-sm text-gray-500">RIB envoyé par email pour les paiements par virement</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                Titulaire du compte
              </label>
              <input
                type="text"
                value={settings.bank_account_holder || ''}
                onChange={(e) => setSettings({...settings, bank_account_holder: e.target.value})}
                placeholder="JOEY CYAN SARL"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                Nom de la banque
              </label>
              <input
                type="text"
                value={settings.bank_name || ''}
                onChange={(e) => setSettings({...settings, bank_name: e.target.value})}
                placeholder="Crédit Agricole"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                IBAN
              </label>
              <input
                type="text"
                value={settings.bank_iban || ''}
                onChange={(e) => setSettings({...settings, bank_iban: e.target.value})}
                placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-green-500 font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                BIC / SWIFT
              </label>
              <input
                type="text"
                value={settings.bank_bic || ''}
                onChange={(e) => setSettings({...settings, bank_bic: e.target.value})}
                placeholder="AGRIFRPPXXX"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-green-500 font-mono"
              />
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-4">
            💡 Ces informations seront envoyées par email aux clients qui souhaitent payer par virement bancaire
          </p>
        </div>

        {/* Contact Information Section */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
              <Mail size={24} className="text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Informations de Contact</h2>
              <p className="text-sm text-gray-500">Email, téléphone et WhatsApp affichés sur le site</p>
            </div>
          </div>

          <ContactSettingsForm />
        </div>

        {/* Social Media Section */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <Share2 size={24} className="text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Réseaux Sociaux</h2>
              <p className="text-sm text-gray-500">Liens affichés dans le footer du site</p>
            </div>
          </div>

          <SocialMediaSettingsForm />
        </div>

        {/* Pack Contents Section */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
              <span className="text-2xl">📦</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Contenu du Pack</h2>
              <p className="text-sm text-gray-500">Liste affichée sur toutes les pages produit</p>
            </div>
          </div>

          <PackContentsForm />
        </div>

        {/* Database Export Section */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-red-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
              <Database size={24} className="text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Export de la Base de Données</h2>
              <p className="text-sm text-gray-500">Télécharger une sauvegarde complète au format JSON</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
            <p className="text-sm text-amber-800">
              ⚠️ <strong>Important :</strong> Cette action va exporter toutes les données de votre base de données MongoDB en un seul fichier JSON. Conservez ce fichier en lieu sûr.
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3 text-sm text-gray-600">
              <Check size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
              <span>Toutes les collections (produits, commandes, utilisateurs, etc.)</span>
            </div>
            <div className="flex items-start gap-3 text-sm text-gray-600">
              <Check size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
              <span>Format JSON lisible et facilement réimportable</span>
            </div>
            <div className="flex items-start gap-3 text-sm text-gray-600">
              <Check size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
              <span>Statistiques incluses (nombre de documents par collection)</span>
            </div>
          </div>

          <button
            onClick={async () => {
              try {
                toast.loading('Export en cours...');
                const { data } = await apiClient.get('/admin/export-database');
                
                // Créer un fichier JSON et le télécharger
                const jsonString = JSON.stringify(data, null, 2);
                const blob = new Blob([jsonString], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `joeycyan_backup_${new Date().toISOString().split('T')[0]}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                
                toast.dismiss();
                toast.success(`✅ Export terminé ! ${data.statistics.total_documents} documents exportés`);
              } catch (err) {
                toast.dismiss();
                toast.error(formatApiError(err));
              }
            }}
            className="w-full mt-4 bg-gradient-to-r from-red-600 to-red-500 text-white px-6 py-3 rounded-xl font-bold hover:from-red-700 hover:to-red-600 transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
          >
            <Download size={20} />
            Télécharger l'export complet (.json)
          </button>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={saveSettings}
            disabled={saving}
            className="bg-[#00aaff] text-white px-8 py-3 rounded-xl font-bold hover:bg-[#0099ee] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Save size={20} />
                Sauvegarder les paramètres
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  // ========== SHIPPING TAB COMPONENT ==========
  const ShippingTab = () => {
    const [shippingMethods, setShippingMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
      name: '',
      description: '',
      price_ttc: 0,
      estimated_days: '',
      is_active: true,
      carrier: '',
      order: 0,
      free_above: '',
      free_above_qty: '',
      countries: []
    });
    const [countryInput, setCountryInput] = useState('');

    useEffect(() => {
      fetchShippingMethods();
    }, []);

    const fetchShippingMethods = async () => {
      try {
        const { data } = await apiClient.get('/admin/shipping-methods');
        setShippingMethods(data);
      } catch (err) {
        toast.error('Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        // Normaliser free_above : '' => null, sinon float
        const payload = {
          ...form,
          free_above: form.free_above === '' || form.free_above === null
            ? null
            : parseFloat(form.free_above),
          free_above_qty: form.free_above_qty === '' || form.free_above_qty === null
            ? null
            : parseInt(form.free_above_qty, 10),
          countries: Array.isArray(form.countries)
            ? form.countries.map(c => String(c).trim()).filter(Boolean)
            : []
        };
        if (editing) {
          await apiClient.put(`/admin/shipping-methods/${editing}`, payload);
          toast.success('Mode de livraison mis à jour !');
        } else {
          await apiClient.post('/admin/shipping-methods', payload);
          toast.success('Mode de livraison créé !');
        }
        resetForm();
        fetchShippingMethods();
      } catch (err) {
        toast.error(formatApiError(err));
      }
    };

    const handleEdit = (method) => {
      setEditing(method.id);
      setForm({
        name: method.name,
        description: method.description || '',
        price_ttc: method.price_ttc,
        estimated_days: method.estimated_days || '',
        is_active: method.is_active !== false,
        carrier: method.carrier || '',
        order: method.order || 0,
        free_above: method.free_above === null || method.free_above === undefined ? '' : method.free_above,
        free_above_qty: method.free_above_qty === null || method.free_above_qty === undefined ? '' : method.free_above_qty,
        countries: Array.isArray(method.countries) ? [...method.countries] : []
      });
      setCountryInput('');
    };

    const handleDelete = async (id) => {
      if (!window.confirm('Supprimer ce mode de livraison ?')) return;
      try {
        await apiClient.delete(`/admin/shipping-methods/${id}`);
        toast.success('Mode de livraison supprimé');
        fetchShippingMethods();
      } catch (err) {
        toast.error(formatApiError(err));
      }
    };

    const resetForm = () => {
      setEditing(null);
      setForm({
        name: '',
        description: '',
        price_ttc: 0,
        estimated_days: '',
        is_active: true,
        carrier: '',
        order: 0,
        free_above: '',
        free_above_qty: '',
        countries: []
      });
      setCountryInput('');
    };

    if (loading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-[#00aaff] border-t-transparent rounded-full animate-spin"></div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Form */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            {editing ? 'Modifier' : 'Ajouter'} un mode de livraison
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                  Nom *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  required
                  placeholder="Livraison standard"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                  Transporteur
                </label>
                <input
                  type="text"
                  value={form.carrier}
                  onChange={(e) => setForm({...form, carrier: e.target.value})}
                  placeholder="Colissimo, Chronopost..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                Description
              </label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                placeholder="Livraison sous 3-7 jours ouvrés"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                  Prix TTC (€) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={form.price_ttc}
                  onChange={(e) => setForm({...form, price_ttc: parseFloat(e.target.value)})}
                  required
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                  Délai estimé
                </label>
                <input
                  type="text"
                  value={form.estimated_days}
                  onChange={(e) => setForm({...form, estimated_days: e.target.value})}
                  placeholder="3-7 jours"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                  Ordre
                </label>
                <input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({...form, order: parseInt(e.target.value)})}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={form.is_active}
                onChange={(e) => setForm({...form, is_active: e.target.checked})}
                className="w-4 h-4 rounded border-gray-300"
              />
              <label htmlFor="is_active" className="text-sm text-gray-700">
                Actif (visible pour les clients)
              </label>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                Gratuit dès (€)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.free_above}
                onChange={(e) => setForm({...form, free_above: e.target.value})}
                placeholder="Ex. 100 — laisser vide pour désactiver"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
              />
              <p className="text-xs text-gray-500 mt-1.5">
                Si le sous-total TTC du panier atteint ce montant, la livraison devient gratuite automatiquement. Laisser vide pour désactiver.
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                Gratuit dès (nb. d'articles / montures)
              </label>
              <input
                type="number"
                step="1"
                min="1"
                value={form.free_above_qty}
                onChange={(e) => setForm({...form, free_above_qty: e.target.value})}
                placeholder="Ex. 3 — laisser vide pour désactiver"
                data-testid="shipping-free-above-qty"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
              />
              <p className="text-xs text-gray-500 mt-1.5">
                Si le panier atteint ce <strong>nombre total d'articles</strong>, la livraison devient gratuite. Fonctionne <strong>indépendamment</strong> du seuil en euros (la première condition remplie déclenche la gratuité). Laisser vide pour désactiver.
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">
                Pays autorisés
              </label>
              <div className="flex flex-wrap gap-2 mb-2" data-testid="shipping-countries-tags">
                {(form.countries || []).length === 0 ? (
                  <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
                    ⚠️ Aucun pays — cette méthode ne sera visible nulle part. Ajoutez au moins un pays ci-dessous.
                  </span>
                ) : (
                  (form.countries || []).map((country, idx) => (
                    <span
                      key={`${country}-${idx}`}
                      className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-3 py-1 text-xs font-medium"
                    >
                      {country}
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, countries: form.countries.filter((_, i) => i !== idx) })}
                        className="hover:text-blue-900"
                        aria-label={`Retirer ${country}`}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={countryInput}
                  onChange={(e) => setCountryInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      const v = countryInput.trim().replace(/,$/, '').trim();
                      if (v && !(form.countries || []).some(c => c.toLowerCase() === v.toLowerCase())) {
                        setForm({ ...form, countries: [...(form.countries || []), v] });
                      }
                      setCountryInput('');
                    }
                  }}
                  placeholder="Tape un pays puis Entrée (ex. France, Belgique, Suisse)"
                  data-testid="shipping-country-input"
                  className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
                />
                <button
                  type="button"
                  onClick={() => {
                    const v = countryInput.trim();
                    if (v && !(form.countries || []).some(c => c.toLowerCase() === v.toLowerCase())) {
                      setForm({ ...form, countries: [...(form.countries || []), v] });
                    }
                    setCountryInput('');
                  }}
                  className="bg-[#00aaff] text-white px-4 py-3 rounded-xl font-semibold hover:bg-[#0099ee] transition-colors text-sm"
                >
                  Ajouter
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1.5">
                Cette méthode ne sera proposée dans le panier qu'aux clients ayant sélectionné <strong>un des pays ci-dessus</strong>.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="bg-[#00aaff] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#0099ee] transition-colors"
              >
                {editing ? 'Mettre à jour' : 'Ajouter'}
              </button>
              {editing && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
              )}
            </div>
          </form>
        </div>

        {/* List */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left p-4 text-xs font-bold text-gray-500 uppercase">Nom</th>
                  <th className="text-left p-4 text-xs font-bold text-gray-500 uppercase">Transporteur</th>
                  <th className="text-left p-4 text-xs font-bold text-gray-500 uppercase">Prix</th>
                  <th className="text-left p-4 text-xs font-bold text-gray-500 uppercase">Délai</th>
                  <th className="text-left p-4 text-xs font-bold text-gray-500 uppercase">Statut</th>
                  <th className="text-right p-4 text-xs font-bold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {shippingMethods.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-8 text-center text-gray-400">
                      Aucun mode de livraison configuré
                    </td>
                  </tr>
                ) : (
                  shippingMethods.map(method => (
                    <tr key={method.id} className="hover:bg-gray-50">
                      <td className="p-4">
                        <div>
                          <p className="font-semibold text-gray-900">{method.name}</p>
                          {method.description && (
                            <p className="text-xs text-gray-500 mt-0.5">{method.description}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-gray-700">{method.carrier || '-'}</span>
                      </td>
                      <td className="p-4">
                        <span className="font-semibold text-gray-900">
                          {method.price_ttc === 0 ? 'Gratuit' : `${method.price_ttc}€`}
                        </span>
                        {method.free_above != null && method.free_above > 0 && (
                          <p className="text-xs text-[#00aaff] mt-0.5">Gratuit dès {method.free_above}€</p>
                        )}
                        {method.free_above_qty != null && method.free_above_qty > 0 && (
                          <p className="text-xs text-[#00aaff] mt-0.5">Gratuit dès {method.free_above_qty} article{method.free_above_qty > 1 ? 's' : ''}</p>
                        )}
                        {Array.isArray(method.countries) && method.countries.length > 0 && (
                          <p className="text-[10px] text-gray-500 mt-1">🌍 {method.countries.slice(0, 4).join(', ')}{method.countries.length > 4 ? ` +${method.countries.length - 4}` : ''}</p>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-gray-700">{method.estimated_days || '-'}</span>
                      </td>
                      <td className="p-4">
                        {method.is_active !== false ? (
                          <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded-full">
                            <Check size={12} />
                            Actif
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 text-xs font-semibold px-2 py-1 rounded-full">
                            <X size={12} />
                            Inactif
                          </span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <button
                            onClick={() => handleEdit(method)}
                            className="text-[#00aaff] hover:text-[#0099ee] p-2"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(method.id)}
                            className="text-red-400 hover:text-red-600 p-2"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };


  const TABS = [
    { key: 'stats', label: 'Statistiques', icon: BarChart3 },
    { key: 'performance', label: 'Performance', icon: TrendingUp },
    { key: 'orders', label: 'Commandes', icon: ShoppingCart },
    { key: 'products', label: 'Produits', icon: Package },
    { key: 'categories', label: 'Catégories', icon: Tag },
    { key: 'attributes', label: 'Attributs', icon: Palette },
    { key: 'blog', label: 'Blog', icon: FileText },
    { key: 'users', label: 'Utilisateurs', icon: Users },
    { key: 'promo', label: 'Codes Promo', icon: Percent },
    { key: 'pro', label: 'Demandes Pro', icon: FileText },
    { key: 'reviews', label: 'Avis', icon: Star },
    { key: 'shipping', label: 'Livraison', icon: Truck },
    { key: 'emails', label: 'Emails', icon: Mail },
    { key: 'faq', label: 'FAQ', icon: FileText },
    { key: 'seo-pages', label: 'SEO Pages', icon: Globe },
    { key: 'settings', label: 'Paramètres', icon: Settings },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00aaff] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f7fa]" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <h1 className="text-3xl font-black text-gray-900 mb-6">Admin Dashboard</h1>

        {/* Tabs */}
        <div className="flex gap-2 flex-wrap mb-6">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              data-testid={`tab-${key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                activeTab === key
                  ? 'bg-[#00aaff] text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-[#00aaff]'
              }`}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-[#e0f4ff] rounded-xl flex items-center justify-center">
                  <ShoppingCart size={20} className="text-[#00aaff]" />
                </div>
                <span className="text-xs text-gray-400 uppercase tracking-wider">Commandes</span>
              </div>
              <p className="text-3xl font-black text-gray-900">{stats.total_orders}</p>
              <p className="text-xs text-yellow-600 mt-1">{stats.pending_orders} en attente</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <BarChart3 size={20} className="text-green-600" />
                </div>
                <span className="text-xs text-gray-400 uppercase tracking-wider">Chiffre d'affaires</span>
              </div>
              <p className="text-3xl font-black text-gray-900">{stats.total_revenue?.toFixed(0)}€</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Users size={20} className="text-purple-600" />
                </div>
                <span className="text-xs text-gray-400 uppercase tracking-wider">Utilisateurs</span>
              </div>
              <p className="text-3xl font-black text-gray-900">{stats.total_users}</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Package size={20} className="text-orange-600" />
                </div>
                <span className="text-xs text-gray-400 uppercase tracking-wider">Produits</span>
              </div>
              <p className="text-3xl font-black text-gray-900">{stats.total_products}</p>
            </div>
          </div>
        )}

        {/* Performance Analytics Tab */}
        {activeTab === 'performance' && (
          <PerformanceAnalytics />
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-6 shadow-sm flex items-center justify-between">
              <h2 className="text-xl font-black text-gray-900">Gestion des Commandes</h2>
              <button
                onClick={() => setShowManualOrderModal(true)}
                className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2.5 rounded-xl font-bold hover:from-green-600 hover:to-green-700 transition-all shadow-sm text-sm"
              >
                <Plus size={18} /> Créer une commande manuelle
              </button>
            </div>

            {orders.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
                <ShoppingCart size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">Aucune commande</p>
              </div>
            ) : (
              orders.map(order => {
                const status = STATUS_LABELS[order.status] || STATUS_LABELS.pending;
                const isExpanded = expandedOrder === order.id;
                return (
                  <div key={order.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <button
                      onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-left">
                          <p className="font-bold text-gray-900">{order.order_number}</p>
                          <p className="text-xs text-gray-400">
                            {order.customer_name} • {new Date(order.created_at).toLocaleDateString('fr-FR')} à {order.created_time || new Date(order.created_at).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}
                          </p>
                        </div>
                        {order.status === 'pending_payment' && (
                          <span className="px-3 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-700 animate-pulse">
                            ⏳ EN ATTENTE PAIEMENT
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <select
                          value={order.status}
                          onChange={(e) => { e.stopPropagation(); updateOrderStatus(order.id, e.target.value); }}
                          onClick={(e) => e.stopPropagation()}
                          className={`text-xs font-bold px-3 py-1 rounded-full border-0 cursor-pointer ${status.color}`}
                        >
                          {STATUS_OPTIONS.map(s => (
                            <option key={s} value={s}>{STATUS_LABELS[s].label}</option>
                          ))}
                        </select>
                        <span className="font-bold text-[#00aaff]">{order.total_ttc?.toFixed(2)}€</span>
                        {isExpanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="px-6 pb-6 border-t border-gray-100 pt-4">
                        <div className="grid md:grid-cols-2 gap-6 mb-4">
                          <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Articles</p>
                            {order.items?.map((item, i) => (
                              <div key={i} className="flex items-center gap-3 mb-2">
                                {item.product_image && <img src={item.product_image} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">{item.product_name}</p>
                                  <p className="text-xs text-gray-400">Qté: {item.quantity} • {item.price}€</p>
                                  {item.color && <p className="text-xs text-gray-400">Couleur: {typeof item.color === 'object' ? item.color.name : item.color}</p>}
                                  {item.lens_option && <p className="text-xs text-gray-400">Verres: {item.lens_option}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Client</p>
                            <p className="text-sm text-gray-600">{order.customer_name}</p>
                            <p className="text-sm text-gray-500">{order.customer_email}</p>
                            {order.customer_phone && <p className="text-sm text-gray-500">{order.customer_phone}</p>}
                            <p className="text-sm text-gray-500 mt-2">{order.shipping_address}</p>
                            {order.tracking_number && (
                              <div className="mt-3 p-2 bg-blue-50 rounded-lg">
                                <p className="text-xs font-semibold text-gray-500">Suivi</p>
                                <p className="text-sm text-gray-700">{order.carrier && `${order.carrier}: `}{order.tracking_number}</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Actions for pending_payment orders */}
                        {order.status === 'pending_payment' && (
                          <div className="mt-6 bg-orange-50 border border-orange-200 rounded-xl p-4">
                            <div className="flex gap-3">
                              <button
                                onClick={() => handleSendPaymentEmail(order.id, 'rib')}
                                className="flex items-center gap-2 bg-white border border-orange-300 text-orange-700 px-4 py-2 rounded-lg font-semibold hover:bg-orange-100 transition-colors text-sm"
                              >
                                <Mail size={16} /> Envoyer RIB par email
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedOrderForPayment(order.id);
                                  setShowMarkAsPaidModal(true);
                                }}
                                className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-600 transition-colors text-sm"
                              >
                                <CreditCard size={16} /> Marquer comme payé
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="flex justify-end gap-3">
                          <button
                            onClick={(e) => { e.stopPropagation(); openEditOrderModal(order); }}
                            className="flex items-center gap-2 bg-amber-500 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-amber-600"
                          >
                            <Edit2 size={14} /> Modifier la commande
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingOrder(order); setShowOrderModal(true); }}
                            className="flex items-center gap-2 bg-[#00aaff] text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-[#0088dd]"
                          >
                            <Settings size={14} /> Gérer cette commande
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteOrder(order.id || order.order_number || order._id); }}
                            className="flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-red-600"
                            title="Supprimer la commande"
                          >
                            <Trash2 size={14} /> Supprimer
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-gray-500">{products.length} produit(s)</p>
              <button
                onClick={() => { setEditingProduct(null); setShowProductModal(true); }}
                data-testid="add-product-btn"
                className="flex items-center gap-2 bg-[#00aaff] text-white px-4 py-2 rounded-xl font-semibold hover:bg-[#0088dd]"
              >
                <Plus size={16} /> Ajouter un produit
              </button>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Produit</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Slug</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Catégorie</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Prix</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Stock</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {[...products].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })).map(p => (
                    <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {p.image_url && <img src={p.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                          <div>
                            <p className="font-semibold text-gray-900">{p.name}</p>
                            <p className="text-xs text-gray-400">{p.subtitle}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-500 font-mono" data-testid={`product-slug-${p.id}`}>
                        {p.slug ? <code className="bg-gray-100 px-2 py-1 rounded">{p.slug}</code> : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-6 py-4 capitalize text-sm text-gray-600">{p.category}</td>
                      <td className="px-6 py-4 font-bold text-[#00aaff]">{p.price}€</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{p.stock}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => { setEditingProduct(p); setShowProductModal(true); }}
                            data-testid={`edit-product-${p.id}`}
                            className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center hover:bg-blue-200"
                            title="Modifier"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => duplicateProduct(p.id)}
                            data-testid={`duplicate-product-${p.id}`}
                            className="w-8 h-8 bg-amber-100 text-amber-600 rounded-lg flex items-center justify-center hover:bg-amber-200"
                            title="Dupliquer"
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            onClick={() => deleteProduct(p.id)}
                            data-testid={`delete-product-${p.id}`}
                            className="w-8 h-8 bg-red-100 text-red-600 rounded-lg flex items-center justify-center hover:bg-red-200"
                            title="Supprimer"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-gray-500">{categories.length} catégorie(s)</p>
              <button
                onClick={() => { setEditingCategory(null); setShowCategoryModal(true); }}
                className="flex items-center gap-2 bg-[#00aaff] text-white px-4 py-2 rounded-xl font-semibold hover:bg-[#0088dd]"
              >
                <Plus size={16} /> Nouvelle catégorie
              </button>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Ordre</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Nom</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Slug</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">URL</th>
                    <th className="text-center px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Statut</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.sort((a, b) => a.order - b.order).map(cat => (
                    <tr key={cat._id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-semibold text-gray-600">{cat.order}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {cat.icon && <span className="text-2xl">{cat.icon}</span>}
                          <div>
                            <p className="font-semibold text-gray-900">{cat.name}</p>
                            {cat.description && <p className="text-xs text-gray-400">{cat.description}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-700">{cat.slug}</code>
                      </td>
                      <td className="px-6 py-4">
                        <a 
                          href={`/collection/${cat.slug}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                        >
                          /collection/{cat.slug}
                        </a>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {cat.is_active ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            <Check size={12} /> Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                            <X size={12} /> Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => { setEditingCategory(cat); setShowCategoryModal(true); }}
                            className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center hover:bg-blue-200"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => deleteCategory(cat._id)}
                            className="w-8 h-8 bg-red-100 text-red-600 rounded-lg flex items-center justify-center hover:bg-red-200"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Attributes Tab */}
        {activeTab === 'attributes' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-gray-500">{attributes.length} attribut(s) couleur</p>
              <button
                onClick={() => { 
                  setEditingAttribute(null); 
                  setShowAttributeModal(true); 
                }}
                className="flex items-center gap-2 bg-[#00aaff] text-white px-4 py-2 rounded-xl font-semibold hover:bg-[#0088dd]"
              >
                <Plus size={16} /> Ajouter une couleur
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {attributes.map(attr => (
                <div key={attr.id} className="bg-white rounded-xl border border-gray-200 p-4 hover:border-[#00aaff] transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <div 
                      className="w-12 h-12 rounded-lg border-2 border-gray-200"
                      style={{ backgroundColor: attr.value || '#FFC0CB' }}
                    />
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900">{attr.name}</h3>
                      <p className="text-xs text-gray-500">Slug: {attr.slug || 'N/A'}</p>
                      <p className="text-xs text-gray-400">{attr.value || '#FFC0CB'}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { 
                        setEditingAttribute(attr); 
                        setShowAttributeModal(true); 
                      }}
                      className="flex-1 flex items-center justify-center gap-2 bg-blue-50 text-blue-600 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-blue-100"
                    >
                      <Edit2 size={14} /> Modifier
                    </button>
                    <button
                      onClick={() => deleteAttribute(attr.id)}
                      className="flex items-center justify-center bg-red-50 text-red-600 px-3 py-2 rounded-lg hover:bg-red-100"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
              {attributes.length === 0 && (
                <div className="col-span-full text-center py-12 bg-white rounded-2xl border border-gray-100">
                  <Palette size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-400">Aucun attribut couleur</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-gray-500">{users.length} utilisateur(s)</p>
              <button
                onClick={() => { setEditingUser(null); setShowUserModal(true); }}
                data-testid="add-user-btn"
                className="flex items-center gap-2 bg-[#00aaff] text-white px-4 py-2 rounded-xl font-semibold hover:bg-[#0088dd]"
              >
                <Plus size={16} /> Ajouter un utilisateur
              </button>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Nom</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Email</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Téléphone</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Société</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Rôle</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">{u.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{u.email}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{u.phone || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{u.company || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                          u.role === 'admin' ? 'bg-red-100 text-red-700' :
                          u.role === 'pro' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {u.role === 'user' ? 'Client' : u.role === 'pro' ? 'Pro' : 'Admin'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => { setEditingUser(u); setShowUserModal(true); }}
                            data-testid={`edit-user-${u.id}`}
                            className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center hover:bg-blue-200"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => deleteUser(u.id)}
                            data-testid={`delete-user-${u.id}`}
                            className="w-8 h-8 bg-red-100 text-red-600 rounded-lg flex items-center justify-center hover:bg-red-200"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pro Requests Tab */}
        {activeTab === 'pro' && (
          <div className="space-y-4">
            {proRequests.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
                <FileText size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">Aucune demande</p>
              </div>
            ) : (
              proRequests.map(req => {
                const status = STATUS_LABELS[req.status] || STATUS_LABELS.pending;
                const contactName = req.first_name && req.last_name 
                  ? `${req.first_name} ${req.last_name}` 
                  : req.contact_name || 'N/A';
                return (
                  <div key={req.id} className="bg-white rounded-2xl border border-gray-100 p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-bold text-gray-900">{req.company_name}</p>
                        <p className="text-sm text-gray-500">{contactName} • {req.email}</p>
                        {req.phone && <p className="text-xs text-gray-400">Tél: {req.phone}</p>}
                        {req.siret && <p className="text-xs text-gray-400">SIRET: {req.siret}</p>}
                        {req.message && <p className="text-sm text-gray-600 mt-2 italic">"{req.message}"</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-3 py-1 rounded-full ${status.color}`}>
                          {status.label}
                        </span>
                        {req.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleProRequest(req.id, 'approve')}
                              data-testid={`approve-pro-${req.id}`}
                              className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center hover:bg-green-200"
                              title="Approuver et créer un compte"
                            >
                              <Check size={16} />
                            </button>
                            <button
                              onClick={() => handleProRequest(req.id, 'reject')}
                              data-testid={`reject-pro-${req.id}`}
                              className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center hover:bg-red-200"
                            >
                              <X size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <ReviewsManager />
        )}

        {/* Promo Codes Tab */}
        {activeTab === 'promo' && (
          <PromoCodesTab />
        )}

        {/* Email Templates Tab */}
        {activeTab === 'emails' && (
          <EmailTemplatesTab />
        )}

        {activeTab === 'blog' && (
          <BlogManager />
        )}

        {activeTab === 'shipping' && (
          <ShippingTab />
        )}

        {activeTab === 'seo-pages' && (
          <SEOPagesManager />
        )}

        {activeTab === 'settings' && (
          <SettingsTab />
        )}

        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-2xl font-black text-gray-900 mb-6">Gestion des FAQ</h2>
            
            {/* FAQ Categories */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">Catégories FAQ</h3>
                <button
                  onClick={() => { setEditingFaqCategory(null); setShowFaqCategoryModal(true); }}
                  className="flex items-center gap-2 bg-[#00aaff] text-white px-4 py-2 rounded-xl font-semibold hover:bg-[#0088dd]"
                >
                  <Plus size={16} /> Nouvelle Catégorie
                </button>
              </div>

              <div className="space-y-2">
                {faqCategories.map((category) => (
                  <div key={category.id} className="border border-gray-200 rounded-lg p-3 flex justify-between items-center">
                    <div>
                      <h4 className="font-semibold text-gray-900">{category.name}</h4>
                      <p className="text-xs text-gray-500">Ordre: {category.order}</p>
                    </div>
                    <button
                      onClick={() => { setEditingFaqCategory(category); setShowFaqCategoryModal(true); }}
                      className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-sm hover:bg-gray-50"
                    >
                      <Edit2 size={14} /> Modifier
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ Items */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">Questions FAQ</h3>
                <button
                  onClick={() => { setEditingFaqItem(null); setShowFaqItemModal(true); }}
                  className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-xl font-semibold hover:bg-green-600"
                >
                  <Plus size={16} /> Nouvelle Question
                </button>
              </div>

              <div className="space-y-3">
                {faqItems.map((item) => {
                  const category = faqCategories.find(c => c.id === item.category_id);
                  return (
                    <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                              {category?.name || 'Sans catégorie'}
                            </span>
                            <span className="text-xs text-gray-500">Ordre: {item.order}</span>
                          </div>
                          <h4 className="font-semibold text-gray-900 mb-2">{item.question}</h4>
                          <p className="text-sm text-gray-600 line-clamp-2">{item.answer}</p>
                        </div>
                        <button
                          onClick={() => { setEditingFaqItem(item); setShowFaqItemModal(true); }}
                          className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-sm hover:bg-gray-50 ml-4"
                        >
                          <Edit2 size={14} /> Modifier
                        </button>
                      </div>
                    </div>
                  );
                })}
                {faqItems.length === 0 && (
                  <p className="text-gray-400 text-center py-8">Aucune question FAQ</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Product Modal */}
      {showProductModal && (
        <ProductModal
          product={editingProduct}
          attributes={attributes}
          categories={categories}
          onClose={() => { setShowProductModal(false); setEditingProduct(null); }}
          onSave={handleProductSave}
        />
      )}

      {/* User Modal */}
      {showUserModal && (
        <UserModal
          user={editingUser}
          onClose={() => { setShowUserModal(false); setEditingUser(null); }}
          onSave={handleUserSave}
        />
      )}

      {/* Manual Order Modal */}
      {showManualOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-black text-gray-900">
                {editingOrderId ? 'Modifier la Commande' : 'Créer une Commande Manuelle'}
              </h3>
              <button 
                onClick={() => { 
                  setShowManualOrderModal(false); 
                  setEditingOrderId(null); 
                }} 
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Client Info + Addresses - Only in creation mode */}
              {!editingOrderId && (
              <>
              <div className="bg-blue-50 p-4 rounded-xl">
                <h4 className="font-bold text-gray-900 mb-3">Informations Client</h4>
                
                {/* Client Selector */}
                <div className="mb-4 p-3 bg-white rounded-lg border-2 border-blue-200">
                  <label className="block text-xs font-bold text-blue-900 mb-2">
                    💡 Sélectionner un client existant
                  </label>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        const selectedUser = users.find(u => u.id === e.target.value);
                        if (selectedUser) {
                          // Pré-remplir le formulaire avec les données du client
                          setManualOrderForm({
                            ...manualOrderForm,
                            customer_first_name: selectedUser.firstName || '',
                            customer_last_name: selectedUser.lastName || '',
                            customer_company: selectedUser.company || '',
                            customer_email: selectedUser.email || '',
                            customer_phone: selectedUser.phone || '',
                            shipping_address: selectedUser.shipping_address?.address || selectedUser.address || '',
                            shipping_postal_code: selectedUser.shipping_address?.postalCode || selectedUser.postalCode || '',
                            shipping_city: selectedUser.shipping_address?.city || selectedUser.city || '',
                            shipping_country: selectedUser.shipping_address?.country || selectedUser.country || 'France',
                            billing_first_name: selectedUser.billing_address?.firstName || selectedUser.firstName || '',
                            billing_last_name: selectedUser.billing_address?.lastName || selectedUser.lastName || '',
                            billing_company: selectedUser.billing_address?.company || selectedUser.company || '',
                            billing_address: selectedUser.billing_address?.address || selectedUser.address || '',
                            billing_postal_code: selectedUser.billing_address?.postalCode || selectedUser.postalCode || '',
                            billing_city: selectedUser.billing_address?.city || selectedUser.city || '',
                            billing_country: selectedUser.billing_address?.country || selectedUser.country || 'France'
                          });
                        }
                      }
                    }}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    <option value="">-- Saisie manuelle --</option>
                    {users.filter(u => u.role !== 'admin').map(user => (
                      <option key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.email}) {user.role === 'pro' ? '👔 Pro' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-2">
                    Vous pouvez modifier les informations après sélection
                  </p>
                </div>
                
                {/* First Name & Last Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Prénom *</label>
                    <input
                      type="text"
                      value={manualOrderForm.customer_first_name}
                      onChange={(e) => setManualOrderForm({...manualOrderForm, customer_first_name: e.target.value})}
                      placeholder="Jean"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Nom *</label>
                    <input
                      type="text"
                      value={manualOrderForm.customer_last_name}
                      onChange={(e) => setManualOrderForm({...manualOrderForm, customer_last_name: e.target.value})}
                      placeholder="Dupont"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      required
                    />
                  </div>
                </div>

                {/* Company */}
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Société (optionnel)</label>
                  <input
                    type="text"
                    value={manualOrderForm.customer_company}
                    onChange={(e) => setManualOrderForm({...manualOrderForm, customer_company: e.target.value})}
                    placeholder="Mon Entreprise SARL"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Email *</label>
                    <input
                      type="email"
                      value={manualOrderForm.customer_email}
                      onChange={(e) => setManualOrderForm({...manualOrderForm, customer_email: e.target.value})}
                      placeholder="jean@example.com"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Téléphone</label>
                    <input
                      type="tel"
                      value={manualOrderForm.customer_phone}
                      onChange={(e) => setManualOrderForm({...manualOrderForm, customer_phone: e.target.value})}
                      placeholder="06 00 00 00 00"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-purple-50 p-4 rounded-xl">
                <h4 className="font-bold text-gray-900 mb-3">Adresse de livraison</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Adresse *</label>
                    <input
                      type="text"
                      value={manualOrderForm.shipping_address}
                      onChange={(e) => setManualOrderForm({...manualOrderForm, shipping_address: e.target.value})}
                      placeholder="10 rue de Paris"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Code postal *</label>
                      <input
                        type="text"
                        value={manualOrderForm.shipping_postal_code}
                        onChange={(e) => setManualOrderForm({...manualOrderForm, shipping_postal_code: e.target.value})}
                        placeholder="75001"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Ville *</label>
                      <input
                        type="text"
                        value={manualOrderForm.shipping_city}
                        onChange={(e) => setManualOrderForm({...manualOrderForm, shipping_city: e.target.value})}
                        placeholder="Paris"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Pays *</label>
                    <input
                      type="text"
                      value={manualOrderForm.shipping_country}
                      onChange={(e) => setManualOrderForm({...manualOrderForm, shipping_country: e.target.value})}
                      placeholder="France"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      required
                    />
                  </div>
                </div>

                {/* Billing Address Toggle */}
                <div className="mt-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={manualOrderForm.use_different_billing}
                      onChange={(e) => setManualOrderForm({...manualOrderForm, use_different_billing: e.target.checked})}
                      className="w-4 h-4 text-[#00aaff] border-gray-300 rounded"
                    />
                    <span className="text-sm font-semibold text-gray-700">Adresse de facturation différente</span>
                  </label>
                </div>

                {/* Billing Address */}
                {manualOrderForm.use_different_billing && (
                  <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Prénom</label>
                        <input
                          type="text"
                          value={manualOrderForm.billing_first_name}
                          onChange={(e) => setManualOrderForm({...manualOrderForm, billing_first_name: e.target.value})}
                          placeholder="Jean"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Nom</label>
                        <input
                          type="text"
                          value={manualOrderForm.billing_last_name}
                          onChange={(e) => setManualOrderForm({...manualOrderForm, billing_last_name: e.target.value})}
                          placeholder="Dupont"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Société</label>
                      <input
                        type="text"
                        value={manualOrderForm.billing_company}
                        onChange={(e) => setManualOrderForm({...manualOrderForm, billing_company: e.target.value})}
                        placeholder="Entreprise"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Adresse</label>
                      <input
                        type="text"
                        value={manualOrderForm.billing_address}
                        onChange={(e) => setManualOrderForm({...manualOrderForm, billing_address: e.target.value})}
                        placeholder="20 avenue de la Facturation"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Code postal</label>
                        <input
                          type="text"
                          value={manualOrderForm.billing_postal_code}
                          onChange={(e) => setManualOrderForm({...manualOrderForm, billing_postal_code: e.target.value})}
                          placeholder="75002"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Ville</label>
                        <input
                          type="text"
                          value={manualOrderForm.billing_city}
                          onChange={(e) => setManualOrderForm({...manualOrderForm, billing_city: e.target.value})}
                          placeholder="Paris"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Pays</label>
                      <input
                        type="text"
                        value={manualOrderForm.billing_country}
                        onChange={(e) => setManualOrderForm({...manualOrderForm, billing_country: e.target.value})}
                        placeholder="France"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
              </>
              )}

              {/* Items */}
              <div className="bg-green-50 p-4 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-gray-900">Articles</h4>
                  <div className="flex gap-2">
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          addItemToManualOrder(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-white"
                    >
                      <option value="">+ Produit existant</option>
                      {products.map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name} - {product.price}€
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => addItemToManualOrder(null)}
                      className="flex items-center gap-1 text-xs bg-green-500 text-white px-3 py-1.5 rounded-lg hover:bg-green-600"
                    >
                      <Plus size={14} /> Article libre
                    </button>
                  </div>
                </div>
                
                {manualOrderForm.items.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">Aucun article ajouté</p>
                )}

                {manualOrderForm.items.map((item, index) => {
                  const selectedProduct = item.product_id ? products.find(p => p.id === item.product_id) : null;
                  const hasColors = selectedProduct?.colors && selectedProduct.colors.length > 0;
                  
                  return (
                  <div key={index} className="bg-white p-3 rounded-lg mb-3 border border-gray-200">
                    {/* Row 1: Product selection */}
                    <div className="grid grid-cols-12 gap-2 mb-2">
                      <div className="col-span-10">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Produit</label>
                        {item.product_id ? (
                          <div className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-gray-50 font-medium">
                            {item.product_name}
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={item.product_name}
                            onChange={(e) => updateManualOrderItem(index, 'product_name', e.target.value)}
                            placeholder="Nom du produit (article libre)"
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                          />
                        )}
                      </div>
                      <div className="col-span-2 flex items-end">
                        <button
                          onClick={() => removeItemFromManualOrder(index)}
                          className="w-full bg-red-50 text-red-600 px-2 py-1.5 rounded-lg hover:bg-red-100 text-xs"
                        >
                          <X size={16} className="mx-auto" />
                        </button>
                      </div>
                    </div>

                    {/* Row 2: Color & Lens Config (if product has options) */}
                    {item.product_id && (hasColors || (selectedProduct?.has_lens_config && selectedProduct?.lens_options?.length > 0)) && (
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        {hasColors && (
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Couleur</label>
                            <select
                              value={item.color || ''}
                              onChange={(e) => updateManualOrderItem(index, 'color', e.target.value)}
                              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                            >
                              {selectedProduct.colors.map((color, i) => {
                                const colorName = typeof color === 'string' ? color : color?.name || 'Sans nom';
                                return (
                                  <option key={i} value={colorName}>{colorName}</option>
                                );
                              })}
                            </select>
                          </div>
                        )}
                        {selectedProduct?.has_lens_config && selectedProduct?.lens_options?.length > 0 && (
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Config. verres</label>
                            <select
                              value={item.lens_option || ''}
                              onChange={(e) => {
                                const selectedOption = selectedProduct.lens_options.find(opt => opt.name === e.target.value);
                                const newItems = [...manualOrderForm.items];
                                
                                // Mettre à jour l'option de verres
                                newItems[index].lens_option = e.target.value;
                                
                                // Mettre à jour le prix avec le modificateur
                                if (selectedOption) {
                                  const basePrice = selectedProduct.price || 0;
                                  newItems[index].price = basePrice + (selectedOption.price_modifier || 0);
                                } else {
                                  // Si aucune option, remettre le prix de base
                                  newItems[index].price = selectedProduct.price || 0;
                                }
                                
                                setManualOrderForm({
                                  ...manualOrderForm,
                                  items: newItems
                                });
                              }}
                              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                            >
                              <option value="">Aucune</option>
                              {selectedProduct.lens_options.map((opt, i) => (
                                <option key={i} value={opt.name}>
                                  {opt.name} {opt.price_modifier > 0 ? `(+${opt.price_modifier}€)` : '(Inclus)'}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Row 3: Price & Quantity */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Prix (€)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={item.price}
                          onChange={(e) => updateManualOrderItem(index, 'price', e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Quantité</label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateManualOrderItem(index, 'quantity', e.target.value)}
                          min="1"
                          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )})}
              </div>

              {/* Shipping & Notes */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Frais de livraison (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={manualOrderForm.shipping_cost}
                    onChange={(e) => setManualOrderForm({...manualOrderForm, shipping_cost: e.target.value})}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Total TTC</label>
                  <div className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 font-bold text-[#00aaff]">
                    {calculateManualOrderTotal().toFixed(2)} €
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Notes internes</label>
                <textarea
                  value={manualOrderForm.notes}
                  onChange={(e) => setManualOrderForm({...manualOrderForm, notes: e.target.value})}
                  placeholder="Ex: Client showroom, paiement en attente..."
                  rows={2}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex gap-3 border-t border-gray-100">
              <button
                onClick={() => setShowManualOrderModal(false)}
                className="flex-1 bg-gray-200 text-gray-700 py-2.5 rounded-lg font-semibold hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateManualOrder}
                disabled={!manualOrderForm.customer_first_name || !manualOrderForm.customer_email || !manualOrderForm.shipping_address}
                className="flex-1 bg-[#00aaff] text-white py-2.5 rounded-lg font-semibold hover:bg-[#0088dd] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingOrderId ? 'Enregistrer les modifications' : 'Créer la commande'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Paid Modal */}
      {showMarkAsPaidModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 rounded-t-2xl">
              <h3 className="text-xl font-black text-white">Marquer comme Payé</h3>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2">Source de paiement *</label>
                <select
                  value={paymentForm.payment_source}
                  onChange={(e) => setPaymentForm({...paymentForm, payment_source: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm"
                >
                  <option value="virement">💳 Virement bancaire</option>
                  <option value="espece">💵 Espèce</option>
                  <option value="cheque">📝 Chèque</option>
                  <option value="carte">💳 Carte bancaire</option>
                  <option value="autre">🔹 Autre</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2">Notes de paiement</label>
                <textarea
                  value={paymentForm.payment_notes}
                  onChange={(e) => setPaymentForm({...paymentForm, payment_notes: e.target.value})}
                  placeholder="Ex: Virement reçu le 04/04/2026, référence XXX"
                  rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex gap-3 rounded-b-2xl">
              <button
                onClick={() => {
                  setShowMarkAsPaidModal(false);
                  setSelectedOrderForPayment(null);
                }}
                className="flex-1 bg-gray-200 text-gray-700 py-2.5 rounded-lg font-semibold hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                onClick={handleMarkAsPaid}
                className="flex-1 bg-green-500 text-white py-2.5 rounded-lg font-semibold hover:bg-green-600 flex items-center justify-center gap-2"
              >
                <Check size={18} /> Confirmer le paiement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {showOrderModal && (
        <OrderDetailsModal
          order={editingOrder}
          onClose={() => { setShowOrderModal(false); setEditingOrder(null); }}
          onUpdate={() => { fetchData(); }}
        />
      )}

      {showCategoryModal && (
        <CategoryModal
          category={editingCategory}
          onClose={() => { setShowCategoryModal(false); setEditingCategory(null); }}
          onSave={handleCategorySave}
        />
      )}

      {/* FAQ Category Modal */}
      {showFaqCategoryModal && (
        <FaqCategoryModal
          category={editingFaqCategory}
          onClose={() => { setShowFaqCategoryModal(false); setEditingFaqCategory(null); }}
          onSave={handleSaveFaqCategory}
          onDelete={handleDeleteFaqCategory}
        />
      )}

      {/* FAQ Item Modal */}
      {showFaqItemModal && (
        <FaqItemModal
          item={editingFaqItem}
          categories={faqCategories}
          onClose={() => { setShowFaqItemModal(false); setEditingFaqItem(null); }}
          onSave={handleSaveFaqItem}
          onDelete={handleDeleteFaqItem}
        />
      )}

      {/* Attribute Modal */}
      {showAttributeModal && (
        <AttributeModal
          attribute={editingAttribute}
          onClose={() => { setShowAttributeModal(false); setEditingAttribute(null); }}
          onSave={handleAttributeSave}
        />
      )}
    </div>
  );
}

// ========== ATTRIBUTE MODAL COMPONENT ==========
function AttributeModal({ attribute, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: attribute?.name || '',
    value: attribute?.value || '#FFC0CB',
    slug: attribute?.slug || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full">
        <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <h3 className="text-xl font-black text-gray-900">
            {attribute ? 'Modifier l\'attribut couleur' : 'Nouvel attribut couleur'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Nom de la couleur *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Bleu Ciel"
              className="w-full border border-gray-200 rounded-lg px-4 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Code couleur (hex) *</label>
            <div className="flex gap-3">
              <input
                type="color"
                value={formData.value}
                onChange={(e) => setFormData({...formData, value: e.target.value})}
                className="w-16 h-12 rounded-lg border border-gray-200 cursor-pointer"
              />
              <input
                type="text"
                value={formData.value}
                onChange={(e) => setFormData({...formData, value: e.target.value})}
                placeholder="#0066CC"
                className="flex-1 border border-gray-200 rounded-lg px-4 py-2 font-mono"
                pattern="^#[0-9A-Fa-f]{6}$"
                required
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Format: #RRGGBB (ex: #0066CC)</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Slug (optionnel)</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({...formData, slug: e.target.value})}
              placeholder="bleu-ciel (généré automatiquement si vide)"
              className="w-full border border-gray-200 rounded-lg px-4 py-2"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 bg-[#00aaff] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#0088dd]"
            >
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ========== EMAIL TEMPLATE MODAL COMPONENT ==========
// ========== FAQ CATEGORY MODAL COMPONENT ==========
function FaqCategoryModal({ category, onClose, onSave, onDelete }) {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    order: category?.order || 0,
    is_active: category?.is_active ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full">
        <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <h3 className="text-xl font-black text-gray-900">
            {category ? 'Modifier la Catégorie FAQ' : 'Nouvelle Catégorie FAQ'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Nom de la catégorie *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Livraison"
              className="w-full border border-gray-200 rounded-lg px-4 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Ordre d'affichage</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
              className="w-full border border-gray-200 rounded-lg px-4 py-2"
            />
            <p className="text-xs text-gray-500 mt-1">Plus le nombre est petit, plus la catégorie apparaît en haut</p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              className="w-4 h-4"
            />
            <label className="text-sm font-semibold text-gray-700">Catégorie active</label>
          </div>

          <div className="flex justify-between items-center pt-4">
            {category && (
              <button
                type="button"
                onClick={() => onDelete(category.id)}
                className="bg-red-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-600"
              >
                Supprimer
              </button>
            )}
            <div className="flex gap-3 ml-auto">
              <button
                type="button"
                onClick={onClose}
                className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="bg-[#00aaff] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#0088dd]"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ========== FAQ ITEM MODAL COMPONENT ==========
function FaqItemModal({ item, categories, onClose, onSave, onDelete }) {
  const [formData, setFormData] = useState({
    category_id: item?.category_id || (categories[0]?.id || ''),
    question: item?.question || '',
    answer: item?.answer || '',
    order: item?.order || 0,
    is_active: item?.is_active ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <h3 className="text-xl font-black text-gray-900">
            {item ? 'Modifier la Question FAQ' : 'Nouvelle Question FAQ'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Catégorie *</label>
            <select
              value={formData.category_id}
              onChange={(e) => setFormData({...formData, category_id: e.target.value})}
              className="w-full border border-gray-200 rounded-lg px-4 py-2"
              required
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Question *</label>
            <input
              type="text"
              value={formData.question}
              onChange={(e) => setFormData({...formData, question: e.target.value})}
              placeholder="Quels sont vos délais de livraison ?"
              className="w-full border border-gray-200 rounded-lg px-4 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Réponse *</label>
            <textarea
              value={formData.answer}
              onChange={(e) => setFormData({...formData, answer: e.target.value})}
              placeholder="Nos délais de livraison sont de..."
              className="w-full border border-gray-200 rounded-lg px-4 py-2"
              rows={6}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Ordre d'affichage</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
              className="w-full border border-gray-200 rounded-lg px-4 py-2"
            />
            <p className="text-xs text-gray-500 mt-1">Plus le nombre est petit, plus la question apparaît en haut</p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              className="w-4 h-4"
            />
            <label className="text-sm font-semibold text-gray-700">Question active</label>
          </div>

          <div className="flex justify-between items-center pt-4">
            {item && (
              <button
                type="button"
                onClick={() => onDelete(item.id)}
                className="bg-red-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-600"
              >
                Supprimer
              </button>
            )}
            <div className="flex gap-3 ml-auto">
              <button
                type="button"
                onClick={onClose}
                className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="bg-[#00aaff] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#0088dd]"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

